/**
 * Reservations admin API wrapper (v2 — Project Independence, DNKADM-0061).
 *
 * Re-points the v1 `/admin/reservations` GoHighLevel passthrough to the **owned,
 * atomic** v2 reservation surface (handoff §5.15): a stable `reservationUuid` that
 * survives reschedule, resolved assigned staff/resource NAMES carried on the DTO,
 * snapshot money/credits, and a read-only cancellation preview. The v1 Any-Bay JSON
 * stamping / correction dance and `policy_chain_id` are superseded (§9) — the bay is
 * just `assignedResourceName`, rendered directly.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.15):**
 *  - `GET    /v2/admin/reservations?locationId=…&…filters` — whole-tenant, but the
 *    `locationId` is **REQUIRED** (an unscoped call is rejected). Defensively parsed.
 *  - `GET    /v2/admin/reservations/{uuid}` → one {@link Reservation}.
 *  - `POST   /v2/admin/reservations` ← {@link CreateReservationInput} (create-on-behalf).
 *  - `PUT    /v2/admin/reservations/{uuid}` ← {@link UpdateReservationInput} (reschedule,
 *    credit-neutral).
 *  - `DELETE /v2/admin/reservations/{uuid}` ← {@link CancelReservationInput}
 *    (`reason` + `directive`). Admin cancel does NOT honor the member refund-lock.
 *  - `GET    /v2/admin/reservations/{uuid}/cancellation-preview` →
 *    {@link CancellationPreview} (read-only refund math; same as the cancel debit).
 *  - `PUT    /v2/admin/reservations/{uuid}/assignment` ← {@link ReassignReservationInput}
 *    (DNKADM-0211 — reassign Staff/Resource on a CONFIRMED booking in place; partial,
 *    money-neutral, no reschedule side-effects; a bay double-book is permitted, a coach
 *    double-book is `409 STAFF_CONFLICT` unless `acknowledgeConflict` — DNKAPI-0542).
 *  - `GET    /v2/admin/reservations/{uuid}/assignment-options` →
 *    {@link AssignmentOptions} (the reassignment picker's pools + conflict marks).
 *
 * **Reservation DTO (handoff §5.15, read on every list/detail/create):**
 * `reservationUuid` (stable public id), `contactId`, `serviceId`/`variationId`/
 * `locationId`, `status` (`new|unconfirmed|confirmed|cancelled|showed|no_show|invalid`),
 * `start`/`end` (**ISO-8601 with offset — rendered AS-IS, never reformatted to local
 * tz**), `durationMinutes`, `assignedStaffId`+`assignedStaffName`,
 * `assignedResourceId`+`assignedResourceName` (the "bay"; resolved live; null when the
 * service uses no staff/resource), `priceCharged`, `taxCharged`, `creditsCharged`,
 * `creditBucketId`, `paymentStatus`, `bookedBy` (`member|guest|admin`), `cancelledBy`/
 * `cancelReason`, `rescheduleCount`. DNKAPI-0547 U2 adds `bookedByActor` — the create-audit
 * row's `{ actorType, label, sessionMode, at }`, which is who ACTUALLY booked it (the shipped
 * `bookedBy` string stays, unchanged and unread by the drawer). DNKADM-0256 additionally reads the booking
 * contact's membership state plus the DNKAPI-0432 `hasLogin` account signal on enriched
 * admin reads. An explicit `false` marks an unclaimed booker and drives the Confirmations
 * Unclaimed chip plus the DNKADM-0280 Invite gate; absent or malformed values stay `null`.
 *
 * **Wire-shape assumptions (recorded in the DNKADM-0061 delivery Notes; the live v2
 * OpenAPI is authoritative when available, §10):** the list envelope is tolerated as a
 * bare array / `reservations` / `content` wrapper; field names follow the handoff
 * verbatim; `start`/`end` are ISO-8601-with-offset strings. Every read is parsed
 * DEFENSIVELY (mirrors `services.ts` / `members.ts`): a missing/wrong-typed field
 * degrades, never a throw.
 *
 * **Calendar back-compat (DNKADM-0061 — "keep the existing calendar/list structure").**
 * The existing Reservations Calendar + member panel + `calendar/derive.ts` consume a
 * stable surface (`bookingId`, `startTime`/`endTime`, `serviceName`, `status`, a
 * `staff` object). The parse populates BOTH the v2 canonical fields AND those
 * back-compat aliases so the calendar keeps rendering on the re-pointed data without a
 * full layout rewrite. The bay-axis still derives from `calendar/derive.ts` over the
 * (now v2-sourced) `staff` alias; the assigned-resource NAME is rendered directly off
 * the DTO. Calendar bay/staff roster lanes now derive from the v2 resource/staff lists.
 */
import { request } from './client';
import { ApiError } from './errors';
import { serverMessage } from './serverMessage';
import { v2ErrorCode } from './v2Errors';
import { parseDeclineReason, type DeclineReason } from '@/utils/declineReason';
import type { ReservationStatus } from '@/domain/v2Enums';
import type { PaymentMode } from '@/api/services';
// The audit feed's session-mode vocabulary (DNKAPI-0088), reused verbatim: DNKAPI-0547 U2
// sources `bookedByActor` FROM the create-audit row, so its `sessionMode` is that same
// field and must not drift into a second, parallel union.
import type { SessionMode } from './audit';

/**
 * A refund **directive** on cancel (handoff §5.15). `policy` applies the lead-time
 * policy refund; `full` forces a full refund (admin-only override of the member
 * refund-lock); `none` forfeits. Omitted → server defaults to `policy`.
 */
export type RefundDirective = 'policy' | 'full' | 'none';

/** Who booked the reservation (handoff §5.15 `bookedBy`). */
type BookedBy = 'member' | 'guest' | 'admin';

/**
 * The actor vocabulary on {@link BookedByActor} (DNKAPI-0547 U2) — the **audit** one,
 * UPPERCASE, and deliberately NOT the lowercase {@link BookedBy} union above. The two
 * fields sit side by side on the same row and mean different things: `bookedBy` is the
 * shipped booking-route string, `bookedByActor.actorType` is the audit row's actor class
 * (and adds `SYSTEM`, which no booking route reports). Never conflate them.
 */
export type BookedByActorType = 'MEMBER' | 'GUEST' | 'ADMIN' | 'SYSTEM';

/**
 * Who actually placed the booking, from its create-audit row (DNKAPI-0547 U2). `null` on
 * the reservation when there is no create-audit row at all.
 */
export interface BookedByActor {
  /** The actor class, or null when the value is absent/unrecognized. */
  actorType: BookedByActorType | null;
  /**
   * The display identity: a resolved NAME on a MEMBER/GUEST row, the login (e.g.
   * `info@simcreek.com`) on an ADMIN/SYSTEM one. `null` when the server could not resolve
   * one — and, per the contract, **never** a raw principal id, so the drawer may render it
   * verbatim without an id ever reaching the screen.
   */
  label: string | null;
  /**
   * The admin session mode the booking was taken in, lowercase and server-owned
   * (`'front-desk' | 'open'`); the client owns the display copy.
   *
   * **`null` means UNKNOWN, never "open".** It is null on every MEMBER/GUEST/SYSTEM row by
   * construction, AND on every row written before DNKAPI-0547 shipped — there is no
   * backfill, so most existing ADMIN bookings carry null here. The drawer copy is built so
   * an unknown mode reads sensibly rather than defaulting to "Front desk".
   *
   * A client-asserted display HINT, not a security signal: no UI may gate on it.
   */
  sessionMode: SessionMode | null;
  /**
   * When the booking was made — ISO-8601 WITH OFFSET in the reservation's location
   * timezone, exactly like `start`/`end` on the same row, and rendered AS-IS (never
   * reformatted to the viewer's local tz).
   */
  at: string | null;
}

/**
 * One owned, atomic reservation (handoff §5.15), defensively parsed. The v2 canonical
 * fields are the source of truth; the trailing back-compat aliases
 * (`bookingId`/`startTime`/`endTime`/`serviceName`/`staff`/`contactName`) keep the
 * existing calendar + member panel rendering on the re-pointed data (DNKADM-0061).
 */
export interface Reservation {
  // --- v2 canonical (handoff §5.15) ---
  /** Stable public id — survives reschedule. The key for detail/cancel/reschedule. */
  reservationUuid: string | null;
  /** Owning contact (external contact-system id) — DATA, never surfaced as "GHL …". */
  contactId: string | null;
  serviceId: string | null;
  variationId: string | null;
  locationId: string | null;
  /** The status (`new|unconfirmed|confirmed|cancelled|showed|no_show|invalid`), or null. */
  status: ReservationStatus | null;
  /** ISO-8601 WITH OFFSET — rendered AS-IS (never reformatted to local tz). */
  start: string | null;
  /** ISO-8601 WITH OFFSET — rendered AS-IS. */
  end: string | null;
  durationMinutes: number | null;
  /** Assigned staff id (the resolved coach), or null when the service uses no staff. */
  assignedStaffId: string | null;
  /** Assigned staff display NAME (rendered directly), or null. */
  assignedStaffName: string | null;
  /** Assigned resource id (the "bay"), or null when the service uses no resource. */
  assignedResourceId: string | null;
  /** Assigned resource display NAME (the "bay"; rendered directly), or null. */
  assignedResourceName: string | null;
  /** Best-effort contact email carried by enriched admin reads, or null. */
  contactEmail: string | null;
  /**
   * Best-effort contact phone (DNKADM-0257 — a pending dink-api addition beside
   * `contactEmail`). Tolerantly parsed; absent → null → the views omit the line.
   */
  contactPhone: string | null;
  /** Live variation display name carried by enriched admin reads, or null. */
  variationName: string | null;
  /** Snapshot price charged (tenant currency, pre-tax), or null. */
  priceCharged: number | null;
  /** Snapshot tax charged (tenant currency), or null. */
  taxCharged: number | null;
  /**
   * The booked service's payment mode (DNKADM-0144 `paymentMode`; additive/null-safe,
   * DNKADM-0146). `'card_on_file'` gates the attendance confirm-charge step (marking
   * Showed/No-show creates the deferred pending charge); `'charge'`/credit reservations
   * settle at booking, so attendance is a plain status change. `null` when the shipped
   * read omits it (degrade: show the confirm-charge step for every attendance mark).
   */
  paymentMode: PaymentMode | null;
  /** Resolved fixed no-show fee in major units, or null when none/absent (DNKAPI-0317). */
  noShowFee: number | null;
  /** Whether this reservation has a vaulted card that can receive a no-show charge. */
  cardOnFile: boolean;
  /** Minimal card display fields for the no-show charge preview. */
  cardBrand: string | null;
  cardLast4: string | null;
  /**
   * Client-derived wire-presence marker. DNKAPI-0317 always supplies the four preview
   * fields (including explicit false/null values); all absent means a pre-0317 read and
   * keeps the legacy generic dialog copy.
   */
  noShowChargePreviewAvailable: boolean;
  /**
   * The card a no-show fee of this booking is charged to (DNKAPI-0677): the card held on
   * the booking (`hold`), or the member's default saved card for a Pay later booking
   * (`saved_card`). `null` when there is none. A list read never looks in the member's
   * wallet, so a Pay later booking reads `null` there until `GET /{uuid}` is read.
   * `undefined` when the field is not on the wire (an older server) — the dialog then
   * falls back to {@link cardOnFile}. Optional so hand-built literals keep compiling.
   */
  noShowCard?: NoShowCard | null | undefined;
  /** The deferred attendance charge attached to this reservation, or null (DNKAPI-0279). */
  pendingCharge: ReservationPendingCharge | null;
  /** Snapshot credits charged, or null. */
  creditsCharged: number | null;
  /** Whether `creditsCharged` was explicitly null or parsed as a finite number on the wire. */
  creditsChargedKnown?: boolean;
  /** The 0052 credit bucket the credits were drawn from, or null (free). */
  creditBucketId: string | null;
  /** Payment status (server text), or null. */
  paymentStatus: string | null;
  /**
   * What the booking still owes — `priceCharged + taxCharged` in the tenant currency
   * (DNKAPI-0559 U5). The server sends it ONLY while `paymentStatus` is `unpaid` (a
   * priced pay-later booking nobody has taken payment for); `null`/absent otherwise.
   * Optional on this interface so the many hand-built `Reservation` literals across the
   * tests keep compiling — an absent value reads exactly like the server's `null`.
   */
  amountDue?: number | null;
  /**
   * `true` exactly while the booking is a card Take payment the processor never answered
   * — `paymentStatus: 'pending'` under a take-payment key (DNKAPI-0563). What Check payment
   * acts on. Absent/odd-typed → `false`, so an older server reads as "nothing to do".
   * Optional for the same reason as {@link amountDue}.
   */
  paymentStranded?: boolean;
  /**
   * `true` while the booking has an OPEN `RESERVATION_PAYMENT_NEEDS_REVIEW` notification
   * (DNKAPI-0563): money dink will not settle on its own, so a manager must look. Drives
   * the `needs-review` payment state. Absent/odd-typed → `false`.
   */
  paymentNeedsReview?: boolean;
  /**
   * The booking's own ISO-4217 currency, carried on the enriched admin read. The list
   * and calendar surfaces render the Unpaid amount from THIS, so no extra
   * `tenant-settings` read is added to the board (which a STAFF session may not make —
   * DNKADM-0364). Absent/malformed → null → the bare label.
   */
  currency?: string | null;
  /**
   * When the booking was made (DNKAPI-0683 V-49): ISO-8601 with the location offset, on every
   * admin reservation read. The Confirmations queue shows how long a request has waited from
   * it (DNKADM-0476 F-04). Absent (an older server) or odd-typed → null → no wait line.
   * Optional for the same reason as {@link amountDue}.
   */
  createdAt?: string | null;
  /**
   * Create-on-behalf response only (DNKAPI-0704): `true` when the member's membership payment
   * has failed and nothing has cleared it. The booking is never refused for it; the dialog
   * uses it as a backstop warning. Absent on every other read and on an older server.
   */
  memberPaymentIssue?: boolean;
  /** Who booked it (`member|guest|admin`), or null. */
  bookedBy: BookedBy | null;
  /**
   * The create-audit row's actor — who placed the booking, when, and in which session mode
   * (DNKAPI-0547 U2). `null` when the reservation has no create-audit row; the drawer then
   * omits its "Booked by" row entirely rather than guessing from {@link bookedBy}, which
   * carries the route but no identity, no timestamp and no mode.
   */
  bookedByActor: BookedByActor | null;
  /** Who cancelled it (server text / id), or null. */
  cancelledBy: string | null;
  /** The recorded cancel reason, or null. */
  cancelReason: string | null;
  /** How many times the reservation has been rescheduled. */
  rescheduleCount: number | null;
  /**
   * The booking contact's DINK membership state (`USER | ACTIVE_MEMBER |
   * INACTIVE_MEMBER`), or null when the read omits it (DNKADM-0256 — a pending
   * dink-api addition sharing DNKADM-0255's account-state contract). Tolerantly
   * parsed; null means "unknown" and the Confirmations queue renders NO badge.
   */
  contactMembershipState: string | null;
  /**
   * Whether a saas-auth platform-user row exists for the booking contact — the
   * DNKAPI-0432 `hasLogin` wire field on enriched admin reads: `true`/`false` only
   * after a successful roster read, `null` when membership is indeterminate.
   * `false` marks an **unclaimed** booker (no login) — it drives the queue's
   * Unclaimed chip and the DNKADM-0280 Invite gate. Supersedes the DNKADM-0261
   * forward-compatible `contactClaimed` read of a `claimed` key dink-api never
   * shipped on this surface. Missing/malformed values stay `null`, never `false`.
   */
  contactHasLogin: boolean | null;

  // --- Back-compat aliases for the existing calendar + member panel ---
  /** Alias of {@link reservationUuid} (the calendar/panel key on `bookingId`). */
  bookingId: string | null;
  /** Alias of {@link start} (the calendar/derive read `startTime`). */
  startTime: string | null;
  /** Alias of {@link end} (the calendar/derive read `endTime`). */
  endTime: string | null;
  /** Display service label for the calendar/legend (the resolved service name). */
  serviceName: string | null;
  /** Display contact label for the calendar/panel, or null (→ contactId fallback). */
  contactName: string | null;
  /**
   * The calendar/derive `staff`-shaped alias, synthesized from the assigned staff (or,
   * when no staff, the assigned RESOURCE so the bay axis still groups). `null` when
   * neither is assigned. `lastName: 'ServiceAccount'` is set for a resource-sourced
   * alias so `deriveBay` keeps treating it as a bay lane (back-compat only).
   */
  staff: ReservationStaffAlias | null;
}

/**
 * The card a no-show fee is charged to (DNKAPI-0677 `NoShowCard`). Display-only: the card
 * id never leaves the server.
 */
export interface NoShowCard {
  /** `hold` — the card held on the booking; `saved_card` — the member's default saved card. */
  source: 'hold' | 'saved_card';
  cardBrand: string | null;
  cardLast4: string | null;
}

/** The charge-only summary carried inline on the enriched reservation read. */
interface ReservationPendingCharge {
  id: number;
  /** Amount in the currency's minor units. */
  amountMinor: number;
  /** ISO-4217 currency code. */
  currency: string;
  /** End + 30-minute run instant, or null when a malformed additive read omits it. */
  scheduledAt: string | null;
  /** Lowercase lifecycle status; unknown future values pass through. */
  status: string;
  /** Why the card was declined once `failed` (DNKAPI-0676), or null when unknown. */
  declineReason?: DeclineReason | null;
}

/**
 * The calendar/derive `staff` alias (DNKADM-0061 back-compat). Mirrors the field set
 * `calendar/derive.ts` reads (`id`/`name`/`firstName`/`lastName`). Synthesized from the
 * v2 assigned staff, or — when the service is resource-only — from the assigned
 * resource (with `lastName: 'ServiceAccount'` so `deriveBay` keeps it on the bay axis).
 */
export interface ReservationStaffAlias {
  id: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
}

/**
 * The server's refund-outcome token on a {@link CancellationPreview} (dink-api
 * `CancellationPreviewResponse.directive`: named `directive` on the wire, but it describes
 * the OUTCOME, never the `policy | full | none` choice that was asked for). The credit
 * tokens cover a credit booking, `NO_CHARGE` a booking that drew nothing (free, comp,
 * pay later, card on file), and the `MONEY_*` tokens a paid money booking (DNKAPI-0611).
 */
export type CancellationOutcome =
  | 'FULL_REFUND'
  | 'PARTIAL_REFUND'
  | 'NO_REFUND'
  | 'REFUND_LOCKED'
  | 'NO_CHARGE'
  | 'MONEY_REFUND'
  | 'MONEY_REFUND_REVIEW'
  | 'MONEY_NO_REFUND';

/**
 * The cancellation preview (handoff §5.15, read-only — same refund math as the actual
 * cancel). `hasConsequence` is true only for a partial credit refund; `refundLocked`
 * reflects the MEMBER refund-lock (an admin may still force `full`). The money fields are
 * set only on a paid money booking (DNKAPI-0611). Defensively parsed; DNKADM-0445
 * realigned it with the shipped wire shape.
 */
export interface CancellationPreview {
  /** The server's refund-outcome token (wire `directive`), or null when absent/unrecognised. */
  outcome: CancellationOutcome | null;
  /** Whether cancelling now would proceed (false for a past booking). */
  cancellable: boolean;
  /**
   * The refund-lock (a rescheduled booking), read from the TOP-LEVEL field only
   * (`summary.refundLocked` is always false). On a locked booking `policy` previews the band
   * amount and `full` the whole debit; the admin may still force `full`.
   */
  refundLocked: boolean;
  /** `start - now` lead time in fractional hours, or null. */
  leadHours: number | null;
  /**
   * The policy window in hours (DNKAPI-0653): the smallest lead time at which the engine
   * gives a 100% refund. Null when there is no bucket or no lead time gives a full refund
   * (and always null on the member preview). Shown as the API gives it.
   */
  policyWindowHours: number | null;
  /** True only when the credit refund is partial (a forfeit consequence). */
  hasConsequence: boolean;
  /** Per-bucket credits the cancel would give back (empty when none). */
  bucketChanges: PreviewBucketChange[];
  /** The shared credit refund block (an object on the wire), or null when absent. */
  summary: CancellationRefundSummary | null;
  /** Money sent back to the payment method, minor units (`MONEY_REFUND`; 0 on `MONEY_NO_REFUND`). */
  refundAmountMinor: number | null;
  /** ISO currency of the money amounts, or null. */
  currency: string | null;
  /** The band percent the money refund was sized at (e.g. 100, 50, 0), or null. */
  refundPercent: number | null;
  /** The settled payment's tenant method label (a desk-recorded method), or null for a card. */
  paymentMethodLabel: string | null;
  /** What the member paid, minor units (`MONEY_NO_REFUND` only), or null. */
  paidAmountMinor: number | null;
  /**
   * DNKAPI-0658 (DNKADM-0452): a refund of the booking’s charge is in flight. Missing on an
   * older API (reads as not in progress).
   */
  refundInProgress?: boolean | undefined;
  /** The in-flight refund’s amount, MAJOR units (summed across the booking’s charges), or null. */
  refundInProgressAmount?: number | null | undefined;
}

/** The credit refund block of a {@link CancellationPreview} (dink-api `RefundSummary`). */
export interface CancellationRefundSummary {
  creditBucketId: string | null;
  /** The credits the booking originally drew. */
  debited: number;
  /** The applied band percent (0 to 100), or null. */
  refundPercent: number | null;
  /** The credits the cancel would give back. */
  refund: number;
}

/** One per-bucket credit restore in a {@link CancellationPreview}. */
interface PreviewBucketChange {
  creditBucketId: string | null;
  /** The bucket display name (DNKAPI-0653), or null when the bucket row does not exist. */
  bucketName: string | null;
  /** Credits given back to the bucket (wire `creditChange`). */
  delta: number;
}

// --- Defensive parsing helpers (mirrors services.ts / members.ts) --------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): the nested entity-reference
 * fk ids (`serviceId`/`variationId`/`locationId`/`assignedStaffId`/`assignedResourceId`/
 * `creditBucketId`) arrive as numbers on the wire and are normalized to the `string` the
 * UI keys off — a finite number → `String(n)`, a non-empty string → trimmed, else null.
 * The reservation's OWN `reservationUuid` and the member's `contactId` are genuine
 * string ids (a UUID / external GHL id) and stay {@link asText} — never coerced.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized oddly), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** A real boolean, or null — never coerced (absent must stay "unknown", DNKADM-0256). */
function asBooleanOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

const RESERVATION_STATUS_SET = new Set<ReservationStatus>([
  'new',
  'unconfirmed',
  'confirmed',
  'cancelled',
  'showed',
  'no_show',
  'invalid',
]);

/** Parse a raw status into a known §4.8 value, or null (unknown/absent degrades). */
function asReservationStatus(value: unknown): ReservationStatus | null {
  const s = asText(value);
  if (s === null) return null;
  const lower = s.toLowerCase();
  return RESERVATION_STATUS_SET.has(lower as ReservationStatus)
    ? (lower as ReservationStatus)
    : null;
}

/** Parse a raw `bookedBy` into a known value, or null. */
function asBookedBy(value: unknown): BookedBy | null {
  const s = asText(value)?.toLowerCase();
  if (s === 'member' || s === 'guest' || s === 'admin') return s;
  return null;
}

/** Parse a raw `bookedByActor.actorType` into a known AUDIT value, or null (unknown degrades). */
function asBookedByActorType(value: unknown): BookedByActorType | null {
  const s = asText(value)?.toUpperCase();
  if (s === 'MEMBER' || s === 'GUEST' || s === 'ADMIN' || s === 'SYSTEM') return s;
  return null;
}

/**
 * Parse a raw `bookedByActor.sessionMode`. An unrecognized value degrades to `null` —
 * which this contract defines as UNKNOWN, never "open", so a future third mode reads as
 * "we don't know" rather than silently claiming the booking was taken in an open session.
 */
function asBookedSessionMode(value: unknown): SessionMode | null {
  const s = asText(value)?.toLowerCase();
  if (s === 'front-desk' || s === 'open') return s;
  return null;
}

/**
 * Parse the additive `bookedByActor` object (DNKAPI-0547 U2). A non-object — absent, null,
 * or malformed — yields `null`, which the drawer reads as "no create-audit row" and omits
 * its row for. Every member degrades independently, mirroring the rest of this parser.
 */
function parseBookedByActor(raw: unknown): BookedByActor | null {
  if (!isObject(raw)) return null;
  return {
    actorType: asBookedByActorType(raw['actorType']),
    label: asText(raw['label']),
    sessionMode: asBookedSessionMode(raw['sessionMode']),
    at: asText(raw['at']),
  };
}

/**
 * Build the calendar/derive `staff` alias (DNKADM-0061 back-compat). Prefers the
 * assigned STAFF (a real coach lane); when the service is resource-only, synthesizes
 * from the assigned RESOURCE and marks `lastName: 'ServiceAccount'` so `deriveBay`
 * keeps it on the bay axis. Null when neither is assigned.
 */
function buildStaffAlias(
  staffId: string | null,
  staffName: string | null,
  resourceId: string | null,
  resourceName: string | null,
): ReservationStaffAlias | null {
  if (staffId !== null) {
    return { id: staffId, name: staffName, firstName: staffName, lastName: null };
  }
  if (resourceId !== null) {
    // Resource-as-bay alias: `firstName` is the bay label, `lastName` flags the bay.
    return { id: resourceId, name: resourceName, firstName: resourceName, lastName: 'ServiceAccount' };
  }
  return null;
}

/** Parse the additive pending-charge summary; malformed/absent summaries degrade to null. */
function parseReservationPendingCharge(raw: unknown): ReservationPendingCharge | null {
  if (!isObject(raw)) return null;
  const id = asNumber(raw['id']);
  if (id === null) return null;
  return {
    id,
    amountMinor: asNumber(raw['amountMinor']) ?? 0,
    currency: asText(raw['currency']) ?? '',
    scheduledAt: asText(raw['scheduledAt']),
    status: asText(raw['status']) ?? '',
    declineReason: parseDeclineReason(raw['declineReason']),
  };
}

/**
 * Parse the additive `noShowCard` (DNKAPI-0677). Absent from the wire → `undefined` (an
 * older server); null, a non-object or an unknown `source` → `null` (no card).
 */
function parseNoShowCard(o: Record<string, unknown>): NoShowCard | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(o, 'noShowCard')) return undefined;
  const raw = o['noShowCard'];
  if (!isObject(raw)) return null;
  const source = asText(raw['source'])?.toLowerCase();
  if (source !== 'hold' && source !== 'saved_card') return null;
  return { source, cardBrand: asText(raw['cardBrand']), cardLast4: asText(raw['cardLast4']) };
}

function parseReservation(raw: unknown): Reservation {
  const o = isObject(raw) ? raw : {};
  // DNKADM-0435 (M-17): the preview is available only when BOTH the fee and the card state
  // are on the wire. A body with a fee but no `cardOnFile` is "card status unknown", never
  // "no card on file" (cardOnFile would default to false and suppress the charge).
  const noShowChargePreviewAvailable = ['noShowFee', 'cardOnFile'].every((key) =>
    Object.prototype.hasOwnProperty.call(o, key),
  );

  const reservationUuid = asText(o['reservationUuid']) ?? asText(o['id']) ?? asText(o['uuid']);
  const start = asText(o['start']) ?? asText(o['startTime']);
  const end = asText(o['end']) ?? asText(o['endTime']);
  const assignedStaffId = asId(o['assignedStaffId']);
  const assignedStaffName = asText(o['assignedStaffName']);
  const assignedResourceId = asId(o['assignedResourceId']);
  const assignedResourceName = asText(o['assignedResourceName']);
  const creditsCharged = asNumber(o['creditsCharged']);
  const creditsChargedKnown = o['creditsCharged'] === null || creditsCharged !== null;
  // The service display name: the owned DTO may carry `serviceName`; tolerate a v1
  // `title` so the legend/cards keep a label.
  const serviceName = asText(o['serviceName']) ?? asText(o['title']);
  const contactName = asText(o['contactName']) ?? asText(o['memberName']);

  return {
    // v2 canonical
    reservationUuid,
    contactId: asText(o['contactId']),
    serviceId: asId(o['serviceId']),
    variationId: asId(o['variationId']),
    locationId: asId(o['locationId']),
    status: asReservationStatus(o['status']),
    start,
    end,
    durationMinutes: asNumber(o['durationMinutes']),
    assignedStaffId,
    assignedStaffName,
    assignedResourceId,
    assignedResourceName,
    contactEmail: asText(o['contactEmail']),
    // DNKADM-0257 — assumed wire key `contactPhone` (best-effort, like contactEmail;
    // Charles's dink-api change has not shipped). Absent → null → the line is omitted.
    contactPhone: asText(o['contactPhone']),
    variationName: asText(o['variationName']),
    priceCharged: asNumber(o['priceCharged']),
    taxCharged: asNumber(o['taxCharged']),
    // Additive/null-safe (DNKADM-0146): the string value when present, else null.
    paymentMode: typeof o['paymentMode'] === 'string' ? (o['paymentMode'] as PaymentMode) : null,
    noShowFee: asNumber(o['noShowFee']),
    cardOnFile: asBoolean(o['cardOnFile'], false),
    cardBrand: asText(o['cardBrand']),
    cardLast4: asText(o['cardLast4']),
    noShowChargePreviewAvailable,
    noShowCard: parseNoShowCard(o),
    pendingCharge: parseReservationPendingCharge(o['pendingCharge']),
    creditsCharged,
    creditsChargedKnown,
    creditBucketId: asId(o['creditBucketId']),
    paymentStatus: asText(o['paymentStatus']),
    // DNKAPI-0559 U5 — present only on an `unpaid` booking; absent/odd-typed → null.
    amountDue: asNumber(o['amountDue']),
    // DNKAPI-0563 — absent/odd-typed → false: an older server reads as "nothing to do".
    paymentStranded: asBoolean(o['paymentStranded'], false),
    paymentNeedsReview: asBoolean(o['paymentNeedsReview'], false),
    currency: asText(o['currency']),
    createdAt: asText(o['createdAt']),
    bookedBy: asBookedBy(o['bookedBy']),
    bookedByActor: parseBookedByActor(o['bookedByActor']),
    cancelledBy: asText(o['cancelledBy']),
    cancelReason: asText(o['cancelReason']),
    rescheduleCount: asNumber(o['rescheduleCount']),
    // DNKADM-0256 membership signal + the DNKAPI-0432 `hasLogin` account signal
    // (shipped on enriched admin reads; explicit false = unclaimed booker →
    // Unclaimed chip + the DNKADM-0280 Invite gate). Tolerant: absent/odd-typed
    // values stay null, never false.
    contactMembershipState: asText(o['membershipState']),
    contactHasLogin: asBooleanOrNull(o['hasLogin']),

    // back-compat aliases (the calendar + member panel + derive read these)
    bookingId: reservationUuid,
    startTime: start,
    endTime: end,
    serviceName,
    contactName,
    staff: buildStaffAlias(assignedStaffId, assignedStaffName, assignedResourceId, assignedResourceName),
  };
}

/**
 * Tolerantly extract the reservation array from a successful list body: a bare array,
 * or a `reservations`/`content`/`bookings` wrapper. A non-array (or missing) yields
 * `[]` rather than a throw.
 */
function extractReservations(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['reservations'])) return body['reservations'];
    if (Array.isArray(body['content'])) return body['content'];
    if (Array.isArray(body['bookings'])) return body['bookings'];
  }
  return [];
}

/** Parse one per-bucket change in a cancellation preview, defensively. */
function parsePreviewBucketChange(raw: unknown): PreviewBucketChange {
  const o = isObject(raw) ? raw : {};
  return {
    creditBucketId: asId(o['creditBucketId']),
    bucketName: asText(o['bucketName']),
    delta: asNumber(o['creditChange']) ?? 0,
  };
}

const CANCELLATION_OUTCOME_SET = new Set<string>([
  'FULL_REFUND',
  'PARTIAL_REFUND',
  'NO_REFUND',
  'REFUND_LOCKED',
  'NO_CHARGE',
  'MONEY_REFUND',
  'MONEY_REFUND_REVIEW',
  'MONEY_NO_REFUND',
] satisfies CancellationOutcome[]);

/** Parse a raw refund-outcome token, or null when absent/unrecognised. */
function asOutcome(value: unknown): CancellationOutcome | null {
  const s = asText(value)?.toUpperCase();
  return s !== undefined && CANCELLATION_OUTCOME_SET.has(s) ? (s as CancellationOutcome) : null;
}

/** Parse the preview's `summary` refund block (an object on the wire), or null. */
function parseRefundSummary(raw: unknown): CancellationRefundSummary | null {
  if (!isObject(raw)) return null;
  return {
    creditBucketId: asId(raw['creditBucketId']),
    debited: asNumber(raw['debited']) ?? 0,
    refundPercent: asNumber(raw['refundPercent']),
    refund: asNumber(raw['refund']) ?? 0,
  };
}

function parseCancellationPreview(raw: unknown): CancellationPreview {
  const o = isObject(raw) ? raw : {};
  const changes = Array.isArray(o['bucketChanges']) ? o['bucketChanges'] : [];
  return {
    outcome: asOutcome(o['directive']),
    cancellable: asBoolean(o['cancellable'], true),
    refundLocked: asBoolean(o['refundLocked'], false),
    leadHours: asNumber(o['leadHours']),
    policyWindowHours: asNumber(o['policyWindowHours']),
    hasConsequence: asBoolean(o['hasConsequence'], false),
    bucketChanges: changes.map(parsePreviewBucketChange),
    summary: parseRefundSummary(o['summary']),
    refundAmountMinor: asNumber(o['refundAmountMinor']),
    currency: asText(o['currency']),
    refundPercent: asNumber(o['refundPercent']),
    paymentMethodLabel: asText(o['paymentMethodLabel']),
    paidAmountMinor: asNumber(o['paidAmountMinor']),
    refundInProgress: o['refundInProgress'] === true,
    refundInProgressAmount: asNumber(o['refundInProgressAmount']),
  };
}

// --- Endpoints -----------------------------------------------------------------

/** v2 admin reservations base path (bearer surface). */
const PATH = '/v2/admin/reservations';

function reservationPath(uuid: string): string {
  return `${PATH}/${encodeURIComponent(uuid)}`;
}

/** The filters both scopings of {@link listReservations} share. */
interface ReservationFilters {
  /** Optional status filter (forwarded when set). */
  status?: ReservationStatus;
  /** Optional contact filter (forwarded when set). */
  contactId?: string;
  /**
   * DNKAPI-0563 `paymentAttention=true`: only bookings whose payment needs attention (a
   * stranded Take payment, or one raised for review). Sent ONLY when `true` — absent is the
   * server's unfiltered list, so every existing request stays byte-identical.
   */
  paymentAttention?: boolean;
}

/**
 * The LOCATION-scoped list — the original shape, unchanged. `locationId` is REQUIRED
 * (the whole-tenant endpoint rejects an unscoped call — handoff §5.15) and the window
 * is `start`/`end`. The active location (DNKADM-0051) supplies `locationId` at every
 * one of these call sites.
 */
export interface LocationReservationsParams extends ReservationFilters {
  /** The active location to scope to — **required** (an unscoped call is a 400). */
  locationId: string;
  /** Optional window start, ISO-8601 with offset (forwarded when set). */
  start?: string;
  /** Optional window end, ISO-8601 with offset (forwarded when set). */
  end?: string;
}

/**
 * The STAFF-scoped list (DNKAPI-0520 / handoff §5, added by DNKADM-0363) — one coach's
 * own bookings, whatever location each sits at. `locationId` is NOT required here: the
 * server scopes by coach, and the location list is scoped by ASSIGNMENT, so filtering a
 * location's bookings client-side would show a different (wrong) set.
 *
 * **The window parameter names are the server's and differ deliberately from the
 * location list's** — `from`/`to` here, `start`/`end` there (handoff §5). They are NOT
 * unified: each name belongs to the endpoint shape that accepts it, and the wire is not
 * this app's to rename.
 */
export interface StaffReservationsParams extends ReservationFilters {
  /** The coach to scope to — **required** for this shape. */
  staffId: string;
  /** Optional window start, ISO-8601 with offset (forwarded when set). */
  from?: string;
  /** Optional window end, ISO-8601 with offset (forwarded when set). */
  to?: string;
  /**
   * Optional location narrowing, forwarded only when set. Added for the DNKADM-0363
   * decision point and now load-bearing (DNKADM-0366): until DNKAPI-0525 ships, an
   * absent `locationId` here does NOT mean "every location in scope," so My schedule
   * calls this once PER assigned location, passing each one's id, and merges the
   * results client-side. After 0525 the unscoped call becomes the primary path again
   * (handoff §5 / `REPLY-to-dink-admin-customer-staff-split.md` §a) and this narrowing
   * reverts to optional.
   */
  locationId?: string;
}

/** Filters for {@link listReservations}: scoped to a LOCATION, or to a COACH. */
export type ListReservationsParams = LocationReservationsParams | StaffReservationsParams;

/** Narrow the union to the staff-scoped shape (`staffId` is its discriminator). */
function isStaffScoped(params: ListReservationsParams): params is StaffReservationsParams {
  return 'staffId' in params && typeof params.staffId === 'string' && params.staffId !== '';
}

/** The server's page-size ceiling for the admin reservation list (`clampSize` in dink-api). */
export const RESERVATION_PAGE_SIZE = 100;
/** The most pages one window read follows — 20 x 100 = 2,000 bookings — before it stops. */
export const RESERVATION_MAX_PAGES = 20;

/** A window read: every booking fetched, and whether the page cap cut it short (DNKADM-0431). */
export interface ReservationWindow {
  reservations: Reservation[];
  /** True when the cap was hit with more pages still on the server. */
  truncated: boolean;
}

/**
 * `GET /v2/admin/reservations?…` → the reservations for ONE scope, parsed defensively.
 * Two scopings, each serialized with the server's OWN parameter names:
 *  - a **location**: `locationId` (REQUIRED — an unscoped call is a `400`) + `start`/`end`;
 *  - a **coach** (DNKAPI-0520): `staffId` + `from`/`to`, with no `locationId`.
 *
 * The server pages (default 20, max 100, newest first) and the envelope's `content` holds
 * one page only, so this follows `page=0,1,…` at `size=100` until `totalPages` (or, for a
 * body with no envelope, a short page) says the window is complete — without that, a window
 * of more than 20 bookings silently lost its earliest ones (DNKADM-0431). It stops at
 * {@link RESERVATION_MAX_PAGES} and reports `truncated` so the screen can say so.
 *
 * Throws a typed `ApiError` on a non-2xx response (the screen renders the 403 "needs
 * OWNER/ADMIN" state).
 */
export async function listReservationWindow(params: ListReservationsParams): Promise<ReservationWindow> {
  const query: Record<string, string | undefined> = {};
  if (isStaffScoped(params)) {
    query['staffId'] = params.staffId;
    if (params.from) query['from'] = params.from;
    if (params.to) query['to'] = params.to;
    if (params.locationId) query['locationId'] = params.locationId;
  } else {
    query['locationId'] = params.locationId;
    if (params.start) query['start'] = params.start;
    if (params.end) query['end'] = params.end;
  }
  if (params.status) query['status'] = params.status;
  if (params.contactId) query['contactId'] = params.contactId;
  if (params.paymentAttention === true) query['paymentAttention'] = 'true';
  query['size'] = String(RESERVATION_PAGE_SIZE);

  const reservations: Reservation[] = [];
  // The server sorts by startTime only (no unique tiebreaker), so a page boundary inside a
  // group of same-start bookings, or a booking created mid-read, can repeat a row on the
  // next page. Keep the first copy; a row with no id can't be compared and is kept.
  const seen = new Set<string>();
  for (let page = 0; page < RESERVATION_MAX_PAGES; page += 1) {
    query['page'] = String(page);
    const body = await request<unknown>('GET', PATH, { query });
    const rows = extractReservations(body);
    for (const row of rows.map(parseReservation)) {
      if (row.bookingId !== null) {
        if (seen.has(row.bookingId)) continue;
        seen.add(row.bookingId);
      }
      reservations.push(row);
    }
    // No numeric `totalPages` means no paging information (a bare array or a bare
    // `{reservations}` body): take that one page and finish.
    const totalPages = isObject(body) ? asNumber(body['totalPages']) : null;
    if (rows.length === 0 || totalPages === null || page + 1 >= totalPages) {
      return { reservations, truncated: false };
    }
  }
  return { reservations, truncated: true };
}

/** The notice a screen shows when {@link ReservationWindow.truncated} — N is the count shown. */
export function truncatedWindowMessage(shown: number): string {
  return `Showing the first ${shown} bookings; narrow the range.`;
}

/** {@link listReservationWindow} for the callers that only need the rows. */
export async function listReservations(params: ListReservationsParams): Promise<Reservation[]> {
  return (await listReservationWindow(params)).reservations;
}

/** Pagination input for the tenant-wide confirmation queue (backend pages are zero-indexed). */
export interface ListUnconfirmedReservationsParams {
  page: number;
  size: number;
  /**
   * Free-text search (DNKAPI-0687): the server matches the booking's stored fields AND the
   * member's roster name or email (case-insensitive substring). Sent only when non-blank.
   */
  q?: string;
}

/** A parsed page from the tenant-wide confirmation queue. */
export interface UnconfirmedReservationsPage {
  content: Reservation[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/** Pagination/filter input for the upcoming card-on-file reservations forecast. */
export interface ListCardOnFileReservationsParams {
  /** Optional future window in hours; omitted means all upcoming card-on-file reservations. */
  withinHours?: number;
  /** Optional location scope; omitted means tenant-wide. */
  locationId?: string;
  /** Backend pages are zero-indexed. */
  page?: number;
  size?: number;
}

/** A parsed page from the upcoming card-on-file reservations forecast. */
export interface CardOnFileReservationsPage {
  content: Reservation[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * `GET /v2/admin/reservations/unconfirmed?page=…&size=…[&q=…]` → the tenant-wide,
 * enriched confirmation queue. This dedicated endpoint deliberately has no
 * `locationId`; the general reservation list remains location-scoped.
 */
export async function listUnconfirmedReservations(
  params: ListUnconfirmedReservationsParams,
): Promise<UnconfirmedReservationsPage> {
  const q = params.q?.trim() ?? '';
  const body = await request<unknown>('GET', `${PATH}/unconfirmed`, {
    query: { page: params.page, size: params.size, ...(q.length > 0 ? { q } : {}) },
  });
  const o = isObject(body) ? body : {};
  const content = extractReservations(o).map(parseReservation);
  return {
    content,
    page: asNumber(o['page']) ?? params.page,
    size: asNumber(o['size']) ?? params.size,
    totalElements: asNumber(o['totalElements']) ?? content.length,
    totalPages: asNumber(o['totalPages']) ?? (content.length === 0 ? 0 : 1),
  };
}

/**
 * `GET /v2/admin/reservations/card-on-file?page=…&size=…` → a read-only,
 * paged forecast of upcoming confirmed reservations with a vaulted card.
 * `withinHours` and `locationId` are forwarded only when set.
 */
export async function listCardOnFileReservations(
  { withinHours, locationId, page = 0, size = 20 }: ListCardOnFileReservationsParams = {},
): Promise<CardOnFileReservationsPage> {
  const query: Record<string, string | number | undefined> = { page, size };
  if (withinHours !== undefined) query['withinHours'] = withinHours;
  if (locationId !== undefined) query['locationId'] = locationId;

  const body = await request<unknown>('GET', `${PATH}/card-on-file`, { query });
  const o = isObject(body) ? body : {};
  const content = extractReservations(o).map(parseReservation);
  return {
    content,
    page: asNumber(o['page']) ?? page,
    size: asNumber(o['size']) ?? size,
    totalElements: asNumber(o['totalElements']) ?? content.length,
    totalPages: asNumber(o['totalPages']) ?? (content.length === 0 ? 0 : 1),
  };
}

/** Input for {@link listPaymentAttentionReservations}. */
export interface ListPaymentAttentionReservationsParams {
  /** The active location to scope to (the Grid's own location). */
  locationId: string;
  /** Backend pages are zero-indexed. */
  page?: number;
  size?: number;
}

/** A parsed page of bookings whose payment needs attention, across all dates. */
export interface PaymentAttentionReservationsPage {
  content: Reservation[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * `GET /v2/admin/reservations?paymentAttention=true&locationId=…&sort=start,asc` → every
 * booking at the location whose payment needs attention (a stranded Take payment, or one
 * raised for review — DNKAPI-0563), WHATEVER its date (DNKADM-0472). No `start`/`end` is
 * sent: an absent bound is unbounded on that side. Oldest first, since the oldest stuck
 * payment is the one most likely to be forgotten. `totalElements` is the count.
 */
export async function listPaymentAttentionReservations({
  locationId,
  page = 0,
  size = 20,
}: ListPaymentAttentionReservationsParams): Promise<PaymentAttentionReservationsPage> {
  const body = await request<unknown>('GET', PATH, {
    query: { locationId, paymentAttention: 'true', sort: 'start,asc', page, size },
  });
  const o = isObject(body) ? body : {};
  const content = extractReservations(o).map(parseReservation);
  return {
    content,
    page: asNumber(o['page']) ?? page,
    size: asNumber(o['size']) ?? size,
    totalElements: asNumber(o['totalElements']) ?? content.length,
    totalPages: asNumber(o['totalPages']) ?? (content.length === 0 ? 0 : 1),
  };
}

/**
 * `GET /v2/admin/reservations/{uuid}` → one reservation (defensively parsed). Throws a
 * typed `ApiError` (`404` NOT_FOUND, upstream forwarded).
 */
export async function getReservation(uuid: string): Promise<Reservation> {
  const body = await request<unknown>('GET', reservationPath(uuid));
  return parseReservation(body);
}

/**
 * How a booking is funded (DNKAPI-0503/0559): draw the member's credits (the server's
 * own default), book unfunded and pay later, charge a card now, or record money taken
 * at the desk (cash / the club's own terminal).
 */
export type ReservationFunding = 'credits' | 'none' | 'card' | 'external';

/**
 * The tender for a `card` / `external` booking payment (DNKAPI-0559) — the SAME field
 * names as the order surface's `payment` object, so the console reuses its controls.
 *
 * **There is deliberately no amount.** The server prices the booking (`price + tax`);
 * the desk chooses HOW it is paid, never HOW MUCH.
 *
 * `cardId` / `cardToken` are OPAQUE credentials: exactly one is sent, and neither is
 * ever rendered or logged.
 */
export interface ReservationPaymentTender {
  /** `card` — a card the member already has on file. Never sent with `cardToken`. */
  cardId?: string;
  /** `card` — a card tokenized at the front desk. Never sent with `cardId`. */
  cardToken?: string;
  /** `external` — REQUIRED: the active tenant payment method the money came in on. */
  paymentMethodId?: string;
  /** `external` — optional `cash|card|external` disambiguator for that method. */
  paymentMethodKind?: string;
}

/**
 * Take-payment body (DNKAPI-0559 U5) — `POST /v2/admin/reservations/{uuid}/payment`.
 * No amount: it is the booking's own `amountDue`. The key is REQUIRED (a replay with
 * the same key returns the same result and never charges twice).
 */
export interface TakeReservationPaymentInput {
  funding: 'card' | 'external';
  payment: ReservationPaymentTender;
  idempotencyKey: string;
}

/**
 * Create-on-behalf body (handoff §5.15, §4.7). `staffId`/`resourceId` are resolved by
 * the service's assignment modes: `MEMBER_SELECTS` REQUIRES the id (else `400
 * STAFF_REQUIRED`/`RESOURCE_REQUIRED`); `ROUND_ROBIN`/`NONE` send NO id. `start` is
 * ISO-8601 with offset. The subject is the target member (`contactId`); the server
 * audits `actor_type=admin`.
 */
export interface CreateReservationInput {
  contactId: string;
  serviceId: string;
  variationId: string;
  locationId: string;
  /** ISO-8601 WITH OFFSET (the chosen availability slot start). */
  start: string;
  /** Resolved by `MEMBER_SELECTS`; omitted for `ROUND_ROBIN`/`NONE`. */
  staffId?: string;
  /** Resolved by `MEMBER_SELECTS`; omitted for `ROUND_ROBIN`/`NONE`. */
  resourceId?: string;
  /**
   * Funding directive (DNKAPI-0503/0559, DNKADM-0349 U2, DNKADM-0402 U1) — `'credits'` draws the normal
   * credit-bucket gate (membership → daily cap → credits → atomic reserve, unchanged);
   * `'none'` skips the credit gate and books unfunded (settled at the counter,
   * `creditsCharged: 0`). Server default is `'credits'` — OMIT this field for that
   * case rather than sending it explicitly (mirrors `staffId`/`resourceId` above: send
   * only the non-default directive). An unrecognised token 400s `INVALID_DIRECTIVE`;
   * this admin book-on-behalf surface is the ONLY caller of `createReservation` in this
   * repo (dink-admin has no member-facing booking flow) — the member-facing surface
   * (a different app entirely) never sends this field.
   */
  funding?: ReservationFunding;
  /**
   * The tender for `funding: 'card' | 'external'` (DNKAPI-0559) — REFUSED with `400`
   * on `credits`/`none`. Omitted for every other funding choice.
   */
  payment?: ReservationPaymentTender;
  /**
   * A client key making a retried create idempotent (a retry returns the same booking
   * and never a second charge or credit debit) — **REQUIRED** by the server for `card` and
   * `external`. The book-on-behalf dialog sends it on every funding choice, credits and
   * pay later included (DNKADM-0477, F-216).
   */
  idempotencyKey?: string;
}

/**
 * `POST /v2/admin/reservations` → create a booking on behalf of a member (the same
 * gated core as a member booking: membership → daily cap → credits → atomic reserve,
 * unless `funding: 'none'` skips the credit gate — DNKAPI-0503). Returns the
 * re-parsed created reservation. Throws a typed `ApiError` carrying the §4.2 booking
 * error code in `details.code` (`SLOT_TAKEN`, `INSUFFICIENT_CREDITS`,
 * `DAILY_CAP_EXCEEDED`, `NO_ACTIVE_MEMBERSHIP`, `SERVICE_HAS_NO_ASSIGNABLE_*`,
 * `STAFF_REQUIRED`/`RESOURCE_REQUIRED`, `INVALID_DIRECTIVE`, …) for `v2ErrorCode` →
 * `serverMessage` mapping.
 */
export async function createReservation(input: CreateReservationInput): Promise<Reservation> {
  const json: Record<string, unknown> = {
    contactId: input.contactId,
    serviceId: input.serviceId,
    variationId: input.variationId,
    locationId: input.locationId,
    start: input.start,
  };
  if (input.staffId !== undefined) json['staffId'] = input.staffId;
  if (input.resourceId !== undefined) json['resourceId'] = input.resourceId;
  if (input.funding !== undefined) json['funding'] = input.funding;
  if (input.payment !== undefined) json['payment'] = input.payment;
  if (input.idempotencyKey !== undefined) json['idempotencyKey'] = input.idempotencyKey;
  const body = await request<unknown>('POST', PATH, { json });
  const created = parseReservation(body);
  // DNKAPI-0704: carried on this create response only; set just when the wire sends a boolean.
  if (isObject(body) && typeof body['memberPaymentIssue'] === 'boolean') {
    created.memberPaymentIssue = body['memberPaymentIssue'];
  }
  return created;
}

/**
 * `POST /v2/admin/reservations/{uuid}/payment` → take a card, or record cash, for ONE
 * existing `unpaid` booking (DNKAPI-0559 U5). The amount is the booking's own
 * `amountDue` — server-owned, never sent from here. Returns the re-parsed (now `paid`)
 * reservation.
 *
 * **This is the ONLY way the console charges an unpaid booking**, and it charges
 * exactly one booking: there is no bulk action and nothing charges at check-in
 * (Operator guardrail, 2026-09-22).
 *
 * Throws a typed `ApiError`: `402 PAYMENT_DECLINED`, `409 PAYMENT_ACTION_REQUIRED`
 * (the card needs the customer's approval, which this surface cannot collect), `409
 * RESERVATION_NOT_UNPAID` (already paid / a payment in flight / cancelled), `403` for a
 * foreign or unknown booking.
 */
export async function takeReservationPayment(
  uuid: string,
  input: TakeReservationPaymentInput,
): Promise<Reservation> {
  const body = await request<unknown>('POST', `${reservationPath(uuid)}/payment`, {
    json: {
      funding: input.funding,
      payment: input.payment,
      idempotencyKey: input.idempotencyKey,
    },
  });
  return parseReservation(body);
}

/**
 * What Check payment found (DNKAPI-0563): `paid` — one charge landed and the booking is
 * settled with it; `unpaid` — nothing landed and the grace period passed, so Take payment
 * may be used again; `in_progress` — the processor has not finished, or it is too soon to
 * tell; `needs_review` — money dink will not settle on its own (a notification names it).
 */
export type PaymentResolveOutcome = 'paid' | 'unpaid' | 'in_progress' | 'needs_review';

/** The Check payment response: the outcome, and the booking after the check. */
export interface ReservationPaymentResolution {
  /** The server's outcome, or null for a value this console does not know. */
  outcome: PaymentResolveOutcome | null;
  reservation: Reservation;
}

function asResolveOutcome(value: unknown): PaymentResolveOutcome | null {
  const s = asText(value)?.toLowerCase();
  if (s === 'paid' || s === 'unpaid' || s === 'in_progress' || s === 'needs_review') return s;
  return null;
}

/**
 * `POST /v2/admin/reservations/{uuid}/payment/resolve` — Check payment (DNKAPI-0563) for a
 * booking whose card Take payment the processor never answered. No body. It reads pay-api's
 * ledger and records what it finds; **it never charges**, and it is safe to repeat.
 *
 * Throws a typed `ApiError`: `409 RESERVATION_PAYMENT_NOT_STRANDED` when the booking has no
 * payment in progress (a normal answer the caller treats as "nothing to check"), `403` for
 * a foreign or unknown booking, and pay-api's own error on an upstream fault (the booking
 * is left exactly as it was).
 */
export async function resolveReservationPayment(
  uuid: string,
): Promise<ReservationPaymentResolution> {
  const body = await request<unknown>('POST', `${reservationPath(uuid)}/payment/resolve`);
  const o = isObject(body) ? body : {};
  return {
    outcome: asResolveOutcome(o['outcome']),
    reservation: parseReservation(o['reservation']),
  };
}

/**
 * What staff decided in Close out (DNKAPI-0565): `settle_to_payment` — the charge they name
 * IS this booking's payment; `no_charge` — nothing was charged, or it was already refunded.
 */
export type PaymentCloseOutResolution = 'settle_to_payment' | 'no_charge';

/** The Close out body. `paymentId` is sent only with `settle_to_payment`. */
export interface CloseOutReservationPaymentInput {
  resolution: PaymentCloseOutResolution;
  /** The pay-api payment id — required with `settle_to_payment`, never sent with `no_charge`. */
  paymentId?: string;
  /** Why staff decided this (1-500 characters, recorded on the audit). */
  reason: string;
}

/** The Close out response. */
export interface ReservationPaymentCloseOut {
  /** The server's resolution, or null for a value this console does not know. */
  resolution: PaymentCloseOutResolution | null;
  /** The booking's OTHER succeeded charges, left untouched — staff refund these by hand. */
  otherSucceededPaymentIds: string[];
  reservation: Reservation;
}

function asCloseOutResolution(value: unknown): PaymentCloseOutResolution | null {
  const s = asText(value)?.toLowerCase();
  if (s === 'settle_to_payment' || s === 'no_charge') return s;
  return null;
}

/**
 * `POST /v2/admin/reservations/{uuid}/payment/close-out` — Close out (DNKAPI-0565), for a
 * stuck card payment Check payment cannot settle on its own. OWNER/ADMIN only (dink-api's
 * class-level `ADMIN_ROLES`). **It never charges or refunds**: it records what staff
 * decided, with a reason. No amount is sent — the console computes none.
 *
 * `paymentId` is put on the wire only for `settle_to_payment`: dink-api refuses one sent
 * with `no_charge` (`400`), an empty string included.
 *
 * Throws a typed `ApiError`: `409 RESERVATION_CLOSE_OUT_PAYMENT_INVALID` when the named
 * payment fails a check (the message says which), `409 RESERVATION_PAYMENT_NOT_STRANDED`
 * when there is no payment in progress any more (the feed or Check payment resolved it
 * first), `400 INVALID_REQUEST` for a bad body, `403` for a non-admin session or a foreign
 * booking, and pay-api's own error on an upstream fault (the booking is left as it was).
 */
export async function closeOutReservationPayment(
  uuid: string,
  input: CloseOutReservationPaymentInput,
): Promise<ReservationPaymentCloseOut> {
  const json: Record<string, unknown> = { resolution: input.resolution, reason: input.reason };
  if (input.resolution === 'settle_to_payment' && input.paymentId !== undefined) {
    json['paymentId'] = input.paymentId;
  }
  const body = await request<unknown>('POST', `${reservationPath(uuid)}/payment/close-out`, {
    json,
  });
  const o = isObject(body) ? body : {};
  const others = Array.isArray(o['otherSucceededPaymentIds'])
    ? o['otherSucceededPaymentIds'].flatMap((id) => {
        const text = asText(id);
        return text === null ? [] : [text];
      })
    : [];
  return {
    resolution: asCloseOutResolution(o['resolution']),
    otherSucceededPaymentIds: others,
    reservation: parseReservation(o['reservation']),
  };
}

/**
 * Reschedule body (handoff §5.15 — credit-neutral). `start` is the new slot start
 * (ISO-8601 with offset); `reason` is the REQUIRED audit reason. Optional
 * `staffId`/`resourceId` mirror the create resolution when the new slot changes the
 * assignment.
 */
export interface UpdateReservationInput {
  /** New start — ISO-8601 with offset. */
  start: string;
  /** Required audit reason (blank → server 400). */
  reason: string;
  staffId?: string;
  resourceId?: string;
  /**
   * DNKAPI-0546: proceed although the new time hands the booking to a DIFFERENT coach.
   * Sent ONLY when `true`; omitted means the server's default — refuse with
   * `409 STAFF_WILL_CHANGE` (see {@link parseStaffChange}). Per request, never
   * remembered, and INDEPENDENT of `acknowledgeConflict` (a different refusal on a
   * different code path — the two never fire on one body).
   */
  acknowledgeStaffChange?: boolean;
  /**
   * DNKAPI-0542: proceed despite an explicit coach pick that double-books. Sent ONLY when
   * `true`. The server's `AdminRescheduleRequest` has always accepted it; it reaches a guard
   * only on a body that carries an explicit `staffId`, since a resolver-assigned coach came
   * out of `freeStaff` and cannot conflict.
   */
  acknowledgeConflict?: boolean;
  /**
   * DNKAPI-0549: proceed although the bay has no capacity left over the new interval. Sent
   * ONLY when `true`; omitted means the server's default — refuse with
   * `409 RESOURCE_CONFLICT` (see {@link parseOverrideRefusal}). Like `acknowledgeConflict`
   * above it is only ever consulted on a body carrying an explicit `resourceId`.
   */
  acknowledgeResourceConflict?: boolean;
  /**
   * DNKAPI-0549: proceed although the coach is outside the hours they work and/or past an
   * appointment cap. **One flag, BOTH reasons** (`STAFF_OUTSIDE_HOURS` and
   * `STAFF_OVER_CAP`). Sent ONLY when `true`; consulted only on an explicit `staffId`.
   */
  acknowledgeStaffSchedule?: boolean;
}

/**
 * `PUT /v2/admin/reservations/{uuid}` → reschedule (credit-neutral). Sends the new
 * `start` + the required `reason` (+ optional resolved ids). Returns the re-parsed
 * reservation — the `reservationUuid` is unchanged (it survives reschedule). Throws a
 * typed `ApiError`.
 *
 * DNKAPI-0546: a new time whose free coach is not the booking's current one is
 * `409 STAFF_WILL_CHANGE` unless `acknowledgeStaffChange`. When NO coach at all is
 * free the older `SLOT_TAKEN` still fires first — a different code, not this one.
 */
export async function updateReservation(
  uuid: string,
  input: UpdateReservationInput,
): Promise<Reservation> {
  const json: Record<string, unknown> = { start: input.start, reason: input.reason };
  if (input.staffId !== undefined) json['staffId'] = input.staffId;
  if (input.resourceId !== undefined) json['resourceId'] = input.resourceId;
  if (input.acknowledgeStaffChange === true) json['acknowledgeStaffChange'] = true;
  if (input.acknowledgeConflict === true) json['acknowledgeConflict'] = true;
  if (input.acknowledgeResourceConflict === true) json['acknowledgeResourceConflict'] = true;
  if (input.acknowledgeStaffSchedule === true) json['acknowledgeStaffSchedule'] = true;
  const body = await request<unknown>('PUT', reservationPath(uuid), { json });
  return parseReservation(body);
}

/**
 * Pre-confirmation details body. Unlike a reschedule, this has no reason and does
 * not consume a reschedule. Assignment ids are supplied only for dimensions whose
 * service assignment mode is `MEMBER_SELECTS`.
 */
export interface UpdateReservationDetailsInput {
  /** Proposed start — ISO-8601 with offset. */
  start: string;
  staffId?: string;
  resourceId?: string;
  /**
   * DNKAPI-0542: proceed despite a coach double-book. Sent ONLY when `true`; omitted
   * means the server's default — refuse with `409 STAFF_CONFLICT` (see
   * {@link parseStaffConflict}). Per request, never remembered.
   */
  acknowledgeConflict?: boolean;
  /**
   * DNKAPI-0546: proceed although the proposed time hands the request to a DIFFERENT
   * coach. Sent ONLY when `true`; omitted means the server's default — refuse with
   * `409 STAFF_WILL_CHANGE` (see {@link parseStaffChange}). Per request, never
   * remembered, and INDEPENDENT of `acknowledgeConflict` above.
   */
  acknowledgeStaffChange?: boolean;
  /**
   * DNKAPI-0549: proceed although the bay has no capacity left over the proposed interval.
   * Sent ONLY when `true`; omitted means the server's default — refuse with
   * `409 RESOURCE_CONFLICT` (see {@link parseOverrideRefusal}). Per request, never
   * remembered, and INDEPENDENT of the two flags above.
   */
  acknowledgeResourceConflict?: boolean;
  /**
   * DNKAPI-0549: proceed although the coach is outside the hours they work and/or past an
   * appointment cap. **One flag, BOTH reasons** (`STAFF_OUTSIDE_HOURS` and
   * `STAFF_OVER_CAP`). Sent ONLY when `true`; omitted means the server's default — refuse.
   */
  acknowledgeStaffSchedule?: boolean;
}

/**
 * `PUT /v2/admin/reservations/{uuid}/details` → conflict-check and update the
 * proposed slot/assignment while keeping the reservation `unconfirmed`. Typed
 * `ApiError` responses, including workflow/conflict `409`s, propagate unchanged.
 * DNKAPI-0542: an explicit `staffId` that sets/changes the coach or moves the time
 * onto a coach's existing booking is `409 STAFF_CONFLICT` unless `acknowledgeConflict`.
 * DNKAPI-0546: with NO explicit `staffId`, a proposed time whose free coach is not the
 * request's current one is `409 STAFF_WILL_CHANGE` unless `acknowledgeStaffChange`.
 */
export async function updateReservationDetails(
  uuid: string,
  input: UpdateReservationDetailsInput,
): Promise<Reservation> {
  const json: Record<string, unknown> = { start: input.start };
  if (input.staffId !== undefined) json['staffId'] = input.staffId;
  if (input.resourceId !== undefined) json['resourceId'] = input.resourceId;
  if (input.acknowledgeConflict === true) json['acknowledgeConflict'] = true;
  if (input.acknowledgeStaffChange === true) json['acknowledgeStaffChange'] = true;
  if (input.acknowledgeResourceConflict === true) json['acknowledgeResourceConflict'] = true;
  if (input.acknowledgeStaffSchedule === true) json['acknowledgeStaffSchedule'] = true;
  const body = await request<unknown>('PUT', `${reservationPath(uuid)}/details`, { json });
  return parseReservation(body);
}

/** The cancel answer (`CancelResponse { cancelled, refund, cardOnFileCharge }`) — not a reservation. */
export interface CancelResponse {
  cancelled: boolean;
  refund: CancellationRefundSummary | null;
}

/** Cancel body (handoff §5.15). `reason` REQUIRED; `directive` defaults to `policy`. */
export interface CancelReservationInput {
  /** Required audit reason (blank → server 400). */
  reason: string;
  /** Refund directive; omitted → server defaults to `policy`. Admin may force `full`. */
  directive?: RefundDirective;
  /**
   * DNKAPI-0660 (DNKADM-0455): the status the caller believed the booking had (lowercase wire
   * value). A decline sends it; if the locked booking differs the server answers
   * `409 RESERVATION_STATUS_CHANGED` (with `currentStatus`) and writes nothing.
   */
  expectedStatus?: string | undefined;
  /**
   * The manager PIN (DNKAPI-0670): a STAFF session's Full or None needs it. Sent only when
   * typed; the server checks it, the console never learns the PIN.
   */
  managerPin?: string;
}

/**
 * `DELETE /v2/admin/reservations/{uuid}` → cancel with a reason + directive. **Admin
 * cancel does NOT honor the member refund-lock** — the server accepts a forced `full`
 * directive from an admin. Returns the server's `CancelResponse` — NOT a reservation
 * (DNKADM-0457), so it is parsed as one and callers refetch for the booking. Throws a
 * typed `ApiError` (`400` blank reason, `403` outside tenant, upstream forwarded).
 */
export async function cancelReservation(
  uuid: string,
  input: CancelReservationInput,
): Promise<CancelResponse> {
  const json: Record<string, unknown> = { reason: input.reason, directive: input.directive ?? 'policy' };
  if (input.expectedStatus) json['expectedStatus'] = input.expectedStatus;
  if (input.managerPin !== undefined) json['managerPin'] = input.managerPin;
  const body = await request<unknown>('DELETE', reservationPath(uuid), { json });
  const o = isObject(body) ? body : {};
  return { cancelled: asBoolean(o['cancelled'], false), refund: parseRefundSummary(o['refund']) };
}

/**
 * `GET /v2/admin/reservations/{uuid}/cancellation-preview[?directive=]` → the read-only
 * refund preview (same math as the actual cancel). Shows the outcome + the member
 * `refundLocked` flag BEFORE confirming (the admin may still force `full`). `directive`
 * previews that one choice (DNKADM-0445 reads all three); omitted, the server resolves it
 * exactly as the cancel would. Throws a typed `ApiError`.
 */
export async function getCancellationPreview(
  uuid: string,
  directive?: RefundDirective,
): Promise<CancellationPreview> {
  const body = await request<unknown>(
    'GET',
    `${reservationPath(uuid)}/cancellation-preview`,
    directive ? { query: { directive } } : {},
  );
  return parseCancellationPreview(body);
}

/**
 * `POST /v2/admin/reservations/{uuid}/check-in` → mark the reservation **Showed**
 * (attendance). FORWARD CONTRACT (dink-api DNKAPI-0279, not yet deployed) — the shipped
 * API wins on any delta. For a **card-on-file** reservation this creates the deferred
 * pending charge at end + 30 min (surfaced in the DNKADM-0145 Upcoming Charges queue),
 * amount = `amountMinorOverride` if supplied else the booked service price; `charge:
 * false` comps it (no pending charge). Marking Showed on a No-show reservation switches
 * the status and re-prices the existing pending charge in place (server-side). Returns
 * the re-parsed (now-showed) reservation. Throws a typed `ApiError` (`409`, upstream).
 */
export async function checkInReservation(
  uuid: string,
  opts: { amountMinorOverride?: number; charge?: boolean; managerPin?: string } = {},
): Promise<Reservation> {
  const json: Record<string, unknown> = { charge: opts.charge ?? true };
  if (opts.amountMinorOverride !== undefined) json['amountMinorOverride'] = opts.amountMinorOverride;
  // DNKAPI-0670: rides with a STAFF session's changed amount; sent only when given.
  if (opts.managerPin !== undefined) json['managerPin'] = opts.managerPin;
  const body = await request<unknown>('POST', `${reservationPath(uuid)}/check-in`, { json });
  return parseReservation(body);
}

/**
 * `POST /v2/admin/reservations/{uuid}/no-show` → mark the reservation **No-show**
 * (attendance). FORWARD CONTRACT (dink-api DNKAPI-0279, not yet deployed) — the shipped
 * API wins on any delta. For a **card-on-file** reservation this creates a pending
 * charge for `amountMinorOverride` when supplied, otherwise the configured no-show fee;
 * `charge:false` waives it (no pending charge).
 * Marking No-show on a Showed reservation switches the status and re-prices the existing
 * pending charge in place (server-side). Returns the re-parsed (now-no-show)
 * reservation. Throws a typed `ApiError` (`409`, upstream). `managerPin` (DNKAPI-0670)
 * rides with a STAFF session's `amountMinorOverride`; sent only when given.
 */
export async function markNoShow(
  uuid: string,
  opts: { amountMinorOverride?: number; charge?: boolean; managerPin?: string } = {},
): Promise<Reservation> {
  const json: Record<string, unknown> = { charge: opts.charge ?? true };
  if (opts.amountMinorOverride !== undefined) json['amountMinorOverride'] = opts.amountMinorOverride;
  if (opts.managerPin !== undefined) json['managerPin'] = opts.managerPin;
  const body = await request<unknown>('POST', `${reservationPath(uuid)}/no-show`, {
    json,
  });
  return parseReservation(body);
}

/**
 * Confirm body (DNKAPI-0542, widened by DNKAPI-0636 D-06). The body is OPTIONAL
 * server-side: with nothing to acknowledge the call sends no body at all (the pre-0542
 * request shape). Each flag is sent ONLY when `true`; omitted = refuse. Per request, never
 * remembered — a host sets one only from {@link overrideAcknowledgements} of a refusal the
 * admin was shown and ticked through.
 */
export interface ConfirmReservationInput {
  /** Proceed despite a coach double-book (`409 STAFF_CONFLICT`). */
  acknowledgeConflict?: boolean;
  /** Proceed despite a bay with no capacity left (`409 RESOURCE_CONFLICT`, DNKAPI-0636). */
  acknowledgeResourceConflict?: boolean;
  /** Proceed despite the coach being off shift or at a cap (`STAFF_OUTSIDE_HOURS` / `STAFF_OVER_CAP`). */
  acknowledgeStaffSchedule?: boolean;
}

/**
 * `POST /v2/admin/reservations/{uuid}/confirm` → confirm an `unconfirmed` booking,
 * moving it to `confirmed`. Mirrors the {@link cancelReservation} action-fn pattern; the
 * typed `ApiError` propagates, including `409 RESERVATION_NOT_UNCONFIRMED` for other
 * workflow states. Already-confirmed is idempotent server-side. Returns the re-parsed
 * (now-confirmed) reservation.
 *
 * DNKAPI-0542: confirm re-checks the coach at confirm time (a pending request can
 * become a conflict after it was made) and refuses with `409 STAFF_CONFLICT` unless
 * the optional body carries `acknowledgeConflict: true`.
 *
 * DNKAPI-0636 D-06: confirm also re-runs the create-time gates, so it can be refused with
 * DNKAPI-0549's `overrideBlocks` body (`RESOURCE_CONFLICT` / `STAFF_OUTSIDE_HOURS` /
 * `STAFF_OVER_CAP`) unless the matching flag is set — read it with
 * {@link parseOverrideRefusal}. Two plain 409s have no flag: `RESOURCE_OUTSIDE_HOURS` (the
 * bay is closed then) and `RESERVATION_CHANGED` (the row changed under the request; reload).
 *
 * Only the flags that are `true` are sent; with none, no body is sent — exactly the
 * pre-0542 request.
 */
export async function confirmReservation(
  uuid: string,
  input: ConfirmReservationInput = {},
): Promise<Reservation> {
  const json: Record<string, unknown> = {};
  if (input.acknowledgeConflict === true) json['acknowledgeConflict'] = true;
  if (input.acknowledgeResourceConflict === true) json['acknowledgeResourceConflict'] = true;
  if (input.acknowledgeStaffSchedule === true) json['acknowledgeStaffSchedule'] = true;
  const options = Object.keys(json).length > 0 ? { json } : {};
  return parseReservation(
    await request<unknown>('POST', `${reservationPath(uuid)}/confirm`, options),
  );
}

// --- Coach double-book refusal (DNKAPI-0542 / DNKADM-0386 U1) ---------------------

/**
 * One booking already in the way of an admin write — an entry of the `conflicts`
 * array a `409 STAFF_CONFLICT` envelope carries (DNKAPI-0542). Every wire entry has
 * all seven keys, the nullable ones as explicit `null`; the parse here still coerces
 * each defensively (a missing/odd-typed field becomes `null`) so the dialog never
 * throws on a partial entry. No email, phone or last name exists on the wire, and none
 * is read.
 */
export interface BookingConflict {
  /** The blocking reservation's stable public id (its `/reservations?reservation=` link). */
  reservationUuid: string | null;
  /** ISO-8601 with offset, in the blocking reservation's location zone — rendered AS-IS. */
  startTime: string | null;
  /** ISO-8601 with offset, same format. */
  endTime: string | null;
  /** The booked service's live display name. */
  serviceName: string | null;
  /** The assigned bay's live name (a booking with no bay is `null`). */
  resourceName: string | null;
  /** The customer's first name only (best-effort). */
  customerFirstName: string | null;
  /** Lowercase wire status (`confirmed` / `unconfirmed` / `new`). */
  status: string | null;
}

/** A `409 STAFF_CONFLICT` refusal as the dialog consumes it: the server's message + the blocking booking(s). */
export interface StaffConflict {
  /** The server `message` ("Jared is already booked at that time."), brand-scrubbed. */
  message: string;
  /** The blocking booking(s) — may be EMPTY when the envelope omits `conflicts` (the dialog still shows the message + both actions). */
  conflicts: BookingConflict[];
}

/**
 * Coerce one wire entry; a non-object entry is dropped. Exported for the staff-remove
 * refusal (`409 STAFF_HAS_UPCOMING_BOOKINGS`, DNKADM-0481), whose `upcomingBookings`
 * entries share this exact shape.
 */
export function parseBookingConflict(raw: unknown): BookingConflict | null {
  if (!isObject(raw)) return null;
  return {
    reservationUuid: asText(raw['reservationUuid']),
    startTime: asText(raw['startTime']),
    endTime: asText(raw['endTime']),
    serviceName: asText(raw['serviceName']),
    resourceName: asText(raw['resourceName']),
    customerFirstName: asText(raw['customerFirstName']),
    status: asText(raw['status']),
  };
}

/**
 * Read a `409 STAFF_CONFLICT` off a thrown error (DNKAPI-0542) — the single seam every
 * DNKADM-0386 U1 host branches on before its ordinary error mapping. Returns the
 * {@link StaffConflict} ONLY when the error carries that code (`v2ErrorCode`), else
 * `null` (every other error keeps its host's existing treatment). Every value is READ
 * from the response (`details.message`, `details.conflicts` as `apiErrorFromBody`
 * preserved them); nothing is reconstructed client-side. A missing / non-array /
 * entry-less `conflicts` yields an empty list, never a throw; an absent message falls
 * back to a neutral line so the dialog title is never blank.
 */
export function parseStaffConflict(error: unknown): StaffConflict | null {
  if (v2ErrorCode(error) !== 'STAFF_CONFLICT' || !(error instanceof ApiError)) return null;
  const raw = error.details?.['conflicts'];
  const conflicts: BookingConflict[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const parsed = parseBookingConflict(entry);
      if (parsed !== null) conflicts.push(parsed);
    }
  }
  const message = serverMessage(error);
  return {
    message:
      message !== null && message.trim().length > 0
        ? message
        : 'This coach is already booked at that time.',
    conflicts,
  };
}

// --- Coach-CHANGE refusal (DNKAPI-0546 / DNKADM-0393 U5) --------------------------

/**
 * One coach named on a `409 STAFF_WILL_CHANGE` envelope — the wire shape is
 * `Staff(Long staffId, @Nullable String displayName)`, so a soft-deleted staff row
 * yields a known id with NO name. `staffId` is normalized to the string the rest of
 * this module keys ids off; **neither field is ever rendered raw at
 * the user** — the prose comes from the server's `reason`, which substitutes "This
 * coach" for a missing name.
 */
export interface StaffChangeStaff {
  /** The staff row's id (a number on the wire), or null when unreadable. */
  staffId: string | null;
  /** The coach's display name — **null for a soft-deleted row**, never rendered raw. */
  displayName: string | null;
}

/**
 * A `409 STAFF_WILL_CHANGE` refusal as the dialog consumes it (DNKAPI-0546): who has
 * the booking now, who would get it, and why.
 */
export interface StaffChange {
  /** The server `message`, brand-scrubbed — the envelope's own summary line. */
  message: string;
  /** The coach on the booking today. Never null on the wire; null here only for a malformed body. */
  currentStaff: StaffChangeStaff | null;
  /**
   * **A THREE-state field — `null` and `undefined` mean different things and must not be
   * conflated at the render boundary:**
   *  - a `StaffChangeStaff` — the coach the new time would hand the booking to;
   *  - **`null`** — the server SAID no coach would be assigned. It sends `null` exactly
   *    when the service's staff mode is `MEMBER_SELECTS`, and the key is always present,
   *    so this is a real answer the console may state as fact;
   *  - **`undefined`** — nothing was readable: the `staffChange` payload was absent,
   *    dropped by the `apiErrorFromBody` allow-list, or the member was an array/scalar.
   *    The server said NOTHING about a proposed coach, so the console must not claim it
   *    said "no coach" — that is a factual claim it never made.
   */
  proposedStaff: StaffChangeStaff | null | undefined;
  /** The server's plain-words explanation ("Ryan Perkins is already booked at that time."), brand-scrubbed. */
  reason: string | null;
}

/**
 * A readable wire object — non-null and **not an array**. The shared {@link isObject} is
 * true for arrays (every other parse here feeds it real object bodies), which would turn a
 * hostile `proposedStaff: []` into a nameless coach the dialog then offers to move to.
 */
function isWireObject(value: unknown): value is Record<string, unknown> {
  return isObject(value) && !Array.isArray(value);
}

/** Coerce one wire staff member; a non-object (or absent, or array) member is null. */
function parseStaffChangeStaff(raw: unknown): StaffChangeStaff | null {
  if (!isWireObject(raw)) return null;
  return { staffId: asId(raw['staffId']), displayName: asText(raw['displayName']) };
}

/**
 * The three-state read of `proposedStaff` — see {@link StaffChange.proposedStaff}. An
 * EXPLICIT `null` is the server's own "no coach would be assigned"; anything unreadable
 * (no payload, a missing key, an array, a scalar) is `undefined`, i.e. "the server said
 * nothing", which the dialog must render as a neutral line rather than as a verdict.
 */
function parseProposedStaff(src: Record<string, unknown> | undefined): StaffChangeStaff | null | undefined {
  if (src === undefined || !('proposedStaff' in src)) return undefined;
  const raw = src['proposedStaff'];
  if (raw === null) return null;
  return isWireObject(raw) ? parseStaffChangeStaff(raw) : undefined;
}

/**
 * Read a `409 STAFF_WILL_CHANGE` off a thrown error (DNKAPI-0546) — the single seam the
 * reschedule and Edit-time hosts branch on before their ordinary error mapping. Returns
 * the {@link StaffChange} ONLY when the error carries that code (`v2ErrorCode`), else
 * `null` (every other error, `STAFF_CONFLICT` included, keeps its host's existing
 * treatment). Every value is READ from the response (`details.message`,
 * `details.staffChange` as `apiErrorFromBody` preserved it); nothing is reconstructed
 * client-side. A missing / unreadable `staffChange` yields `undefined` for
 * `proposedStaff` — **never `null`**, which is the server's own "no coach would be
 * assigned" and a claim the console must not invent — and an absent message falls back to
 * a neutral line so the dialog is never blank.
 */
export function parseStaffChange(error: unknown): StaffChange | null {
  if (v2ErrorCode(error) !== 'STAFF_WILL_CHANGE' || !(error instanceof ApiError)) return null;
  const raw = error.details?.['staffChange'];
  const src = isWireObject(raw) ? raw : undefined;
  const message = serverMessage(error);
  return {
    message:
      message !== null && message.trim().length > 0
        ? message
        : 'This change would move the booking to a different coach.',
    currentStaff: parseStaffChangeStaff(src?.['currentStaff']),
    proposedStaff: parseProposedStaff(src),
    // Raw, like every other field this module parses — the brand scrub happens at the
    // render boundary (the dialog), which is where `parseStaffConflict`'s fields are
    // scrubbed too. `message` arrives already scrubbed via `serverMessage`.
    reason: asText(src?.['reason']),
  };
}

// --- One refusal, however many reasons (DNKAPI-0549 / DNKADM-0396) ----------------

/**
 * The acknowledge flags an admin override refusal can name, as the SERVER spells them
 * (`AdminOverrideGuard.ACKNOWLEDGE_RESOURCE_CONFLICT` / `ACKNOWLEDGE_STAFF_SCHEDULE`, plus
 * DNKAPI-0542's older `acknowledgeConflict`). Each answers a different question and they
 * are never merged: `acknowledgeStaffSchedule` is ONE flag covering BOTH
 * `STAFF_OUTSIDE_HOURS` and `STAFF_OVER_CAP` — there is no fourth flag here.
 *
 * `acknowledgeStaffChange` (DNKAPI-0546) is deliberately NOT in this list: it keeps its
 * own dialog (DNKADM-0393 U5), and a "Book anyway" that set it would acknowledge a
 * decision the admin was never shown.
 */
export const OVERRIDE_ACKNOWLEDGE_FIELDS = [
  'acknowledgeConflict',
  'acknowledgeResourceConflict',
  'acknowledgeStaffSchedule',
] as const;

/** One of the acknowledge flags this console knows how to send. */
export type OverrideAcknowledgeField = (typeof OVERRIDE_ACKNOWLEDGE_FIELDS)[number];

/** The acknowledge flags a resend may carry — the shape every host spreads onto its body. */
export interface OverrideAcknowledgements {
  acknowledgeConflict?: boolean;
  acknowledgeResourceConflict?: boolean;
  acknowledgeStaffSchedule?: boolean;
}

/**
 * ONE reason an admin write was refused — either DNKAPI-0542's coach double-book or one
 * entry of DNKAPI-0549's `overrideBlocks` array.
 */
export interface ConflictReason {
  /** The reason's own backend code, or `null` when the body named none. */
  code: string | null;
  /** The server's plain-words reason, brand-scrubbed. Never blank. */
  message: string;
  /**
   * The request field that overrides **this** reason, as the server named it. `null` when
   * the body named none — the dialog still shows the reason, but no resend can clear it.
   */
  acknowledgeField: string | null;
  /**
   * The booking(s) in the way.
   *
   * **`undefined` and `[]` are different answers and must not be conflated at the render
   * boundary** (the DNKADM-0395 U3 lesson): `undefined` means the server named NO array at
   * all — a `STAFF_OUTSIDE_HOURS` / `STAFF_OVER_CAP` block carries an explicit `null`,
   * because nothing is "in the way" of a coach who is simply not working — so the console
   * must say nothing whatever about conflicts for it. `[]` means the server sent an array
   * and it was empty. Neither may be rendered as a positive "no conflicts" claim.
   */
  conflicts: BookingConflict[] | undefined;
}

/**
 * An admin write refused for one or more reasons, as the shared dialog consumes it — the
 * single view both DNKAPI-0542's `STAFF_CONFLICT` and DNKAPI-0549's `overrideBlocks` are
 * read into, so every host has ONE branch and the desk sees ONE dialog.
 */
export interface ConflictRefusal {
  /** The envelope's own summary `message`, brand-scrubbed — the dialog title. Never blank. */
  message: string;
  /** Every reason the server returned, in the server's own order. Never empty. */
  reasons: ConflictReason[];
}

/**
 * The codes whose envelope carries `overrideBlocks` (DNKAPI-0549). A closed set on purpose:
 * an unknown future code must not be read as an override refusal, because the console would
 * then offer a "Book anyway" for a rule it has no flag to clear.
 */
const OVERRIDE_BLOCKED_CODES: readonly string[] = [
  'RESOURCE_CONFLICT',
  'STAFF_OUTSIDE_HOURS',
  'STAFF_OVER_CAP',
];

/**
 * Coerce one `overrideBlocks` entry; a non-object entry is dropped. `message` is left RAW,
 * like every other field this module parses — the brand scrub happens at the render
 * boundary, exactly as it does for {@link StaffChange.reason}.
 */
function parseOverrideBlock(raw: unknown): ConflictReason | null {
  if (!isWireObject(raw)) return null;
  const message = asText(raw['message'])?.trim() ?? '';
  const nested = raw['resourceConflicts'];
  // Absent key / explicit null / a non-array → `undefined` ("the server named none"), NOT
  // an empty list. Only a real array becomes a list, and its entries are coerced.
  let conflicts: BookingConflict[] | undefined;
  if (Array.isArray(nested)) {
    conflicts = [];
    for (const entry of nested) {
      const parsed = parseBookingConflict(entry);
      if (parsed !== null) conflicts.push(parsed);
    }
  }
  return {
    code: asText(raw['code']),
    message: message.length > 0 ? message : 'This change was refused.',
    acknowledgeField: asText(raw['acknowledgeField']),
    conflicts,
  };
}

/**
 * Read a DNKAPI-0542 `STAFF_CONFLICT` — **and only that** — into the shared dialog's shape.
 *
 * It is the DNKAPI-0542 rule alone, and its one live caller is {@link parseOverrideRefusal},
 * which layers the DNKAPI-0549 reasons on top of it. Create-on-behalf used to read it
 * directly; DNKADM-0457 removed that (the create route never raises `STAFF_CONFLICT`:
 * `AdminReservationCreateRequest` carries no acknowledge field, and neither
 * `StaffConflictGuard` nor `AdminOverrideGuard` is on that path).
 *
 * Returns `null` for every other error, `STAFF_CONFLICT`'s DNKAPI-0549 siblings included.
 */
export function parseStaffConflictRefusal(error: unknown): ConflictRefusal | null {
  const staffConflict = parseStaffConflict(error);
  if (staffConflict === null || !(error instanceof ApiError)) return null;
  return {
    message: staffConflict.message,
    reasons: [
      {
        code: 'STAFF_CONFLICT',
        message: staffConflict.message,
        acknowledgeField: 'acknowledgeConflict',
        // Same absent-vs-empty rule as a block's: `parseStaffConflict` flattens a missing
        // array to `[]` for its own callers, so read presence off the envelope here.
        conflicts: Array.isArray(error.details?.['conflicts'])
          ? staffConflict.conflicts
          : undefined,
      },
    ],
  };
}

/**
 * Read an admin-override refusal off a thrown error — **the single seam every DNKADM-0396
 * host branches on** before its ordinary error mapping. It covers both shapes, because a
 * host cannot know in advance which it will get:
 *
 *  - `409 STAFF_CONFLICT` (DNKAPI-0542) → one reason, its blocking bookings from the
 *    envelope's `conflicts`, overridden by `acknowledgeConflict`. Delegates to
 *    {@link parseStaffConflictRefusal} (and through it to the shipped
 *    {@link parseStaffConflict}) so that read is reused rather than re-implemented.
 *  - `409 RESOURCE_CONFLICT` / `STAFF_OUTSIDE_HOURS` / `STAFF_OVER_CAP` (DNKAPI-0549) → one
 *    reason per `overrideBlocks` entry, each naming its own `acknowledgeField`, the bay's
 *    blocking bookings on the entry's own `resourceConflicts`.
 *
 * The two shapes **never arrive in one body**: dink-api runs `StaffConflictGuard` first and
 * `AdminOverrideGuard` second, so the first to refuse is the one that throws
 * (`AdminOverrideGuard` class javadoc). This function therefore returns one shape or the
 * other, never a merge of both.
 *
 * Returns `null` for every other error (which keeps its host's existing treatment), and for
 * an override code whose `overrideBlocks` the body omitted or left unreadable — with no
 * reason to show there is no dialog to show, and the host's ordinary error mapping is a
 * truer answer than an empty one. Every value is READ from the response; nothing is
 * reconstructed client-side. Never throws.
 *
 * **Use this only on a surface that calls an admin override path** — reassign
 * (`PUT …/assignment`), pre-confirmation details (`PUT …/details`), reschedule
 * (`PUT …/{uuid}`) and, since DNKAPI-0636 D-06, confirm (`POST …/confirm`). Create-on-behalf
 * has no refusal dialog (DNKADM-0457): it cannot be refused for a DNKAPI-0549 reason, and
 * claiming otherwise would be coverage on paper only.
 */
export function parseOverrideRefusal(error: unknown): ConflictRefusal | null {
  const staffConflict = parseStaffConflictRefusal(error);
  if (staffConflict !== null) return staffConflict;

  const code = v2ErrorCode(error);
  if (
    !(error instanceof ApiError) ||
    code === undefined ||
    !OVERRIDE_BLOCKED_CODES.includes(code)
  ) {
    return null;
  }
  const raw = error.details?.['overrideBlocks'];
  if (!Array.isArray(raw)) return null;
  const reasons: ConflictReason[] = [];
  for (const entry of raw) {
    const parsed = parseOverrideBlock(entry);
    if (parsed !== null) reasons.push(parsed);
  }
  const first = reasons[0];
  if (first === undefined) return null;
  const message = serverMessage(error);
  // The envelope's message IS the first block's by construction (the server's fixed
  // precedence), so falling back to it keeps the title truthful rather than inventing one.
  return {
    message: message !== null && message.trim().length > 0 ? message : first.message,
    reasons,
  };
}

/**
 * The acknowledge flags a "Book anyway" resend must carry — **exactly the ones THIS refusal
 * named, and nothing else.** That is the whole safety property: a single "proceed anyway"
 * control can never set a flag for a decision the admin was not shown
 * (dink-api `OverrideBlock` javadoc). One flag may cover several reasons —
 * `acknowledgeStaffSchedule` answers both `STAFF_OUTSIDE_HOURS` and `STAFF_OVER_CAP` — so
 * the result is deduplicated by construction.
 *
 * A reason whose `acknowledgeField` is absent, or names a field this console does not know
 * how to send, contributes NOTHING rather than a guess. {@link canBookAnyway} is how a host
 * asks whether any flag came back at all, so it never offers an override it cannot perform.
 */
export function overrideAcknowledgements(refusal: ConflictRefusal): OverrideAcknowledgements {
  const out: OverrideAcknowledgements = {};
  for (const reason of refusal.reasons) {
    const field = reason.acknowledgeField;
    if (field === null) continue;
    if ((OVERRIDE_ACKNOWLEDGE_FIELDS as readonly string[]).includes(field)) {
      out[field as OverrideAcknowledgeField] = true;
    }
  }
  return out;
}

/**
 * True when the refusal named at least one acknowledge flag this console can send — i.e.
 * when "Book anyway" would actually change the outcome. False hides that action rather than
 * offering a button that resends the identical body and refuses identically.
 */
export function canBookAnyway(refusal: ConflictRefusal): boolean {
  return Object.keys(overrideAcknowledgements(refusal)).length > 0;
}

// --- Reassign staff/resource on a CONFIRMED booking (DNKADM-0211) ---------------

/**
 * The reassign surface's fine-grained backend codes — **the single place these
 * strings live** (DNKADM-0211 U1). Reconciled against the SHIPPED dink-api
 * deliveries `DNKAPI-0356` ("Error outcomes") and `DNKAPI-0357` ("Remaining on
 * `PUT …/assignment`"); update HERE if the API ever renames one.
 *
 * The UI branches **HTTP-status-first** (`ApiError.code`/`status`) and consults
 * these only to disambiguate within a status — the codes reach callers via
 * `v2ErrorCode(err)` (`details.code`, `./v2Errors`).
 *
 * Deliberately NOT added to `V2_ERROR_CODES`: that catalog is the handoff §4.2
 * booking/availability set, and these are reassign-specific.
 */
export const REASSIGN_ERROR_CODES = {
  /** 400 — both ids null (a no-op request). Guarded client-side; never sent. */
  assignmentRequired: 'ASSIGNMENT_REQUIRED',
  /** 400 — staff mode is `none`, or the id is outside the service's staff pool. */
  staffNotAssignable: 'STAFF_NOT_ASSIGNABLE',
  /** 400 — the resource twin of {@link REASSIGN_ERROR_CODES.staffNotAssignable}. */
  resourceNotAssignable: 'RESOURCE_NOT_ASSIGNABLE',
  /** 409 — the booking is `new`/`unconfirmed`; use `PUT …/{uuid}/details` instead. */
  notConfirmed: 'RESERVATION_NOT_CONFIRMED',
  /** 409 — past the appointment + reconciliation grace; check in / mark no-show. */
  past: 'RESERVATION_PAST',
  /** 409 — the booked SERVICE itself is inactive (narrowed by DNKAPI-0444; the
   * variation/location cases below used to hide under this code). */
  serviceInactive: 'SERVICE_INACTIVE',
  /** 409 — the booked variation is inactive/soft-deleted (DNKAPI-0444 split). */
  variationInactive: 'VARIATION_INACTIVE',
  /**
   * 409 — the variation is active but belongs to a different service. Unreachable
   * from the reassign surface by construction (it only re-checks the reservation's
   * own already-paired `variationId`, and a variation's `serviceId` is immutable);
   * mapped defensively as a dead end (DNKADM-0289).
   */
  variationNotForService: 'VARIATION_NOT_FOR_SERVICE',
  /** 409 — the booked location is inactive/soft-deleted (DNKAPI-0444 split). */
  locationInactive: 'LOCATION_INACTIVE',
} as const;

/**
 * A service's assignment mode for one dimension (lowercase FE hints, DNKAPI-0357).
 * A `none` dimension returns an EMPTY option list — the picker hides that dropdown.
 *
 * This and the two option shapes below are INTERNAL (like `BookedBy` /
 * `ReservationPendingCharge`): they are reached through the exported
 * {@link AssignmentOptions}, so exporting them now would only be an unused export.
 */
type AssignmentMode = 'member_selects' | 'round_robin' | 'none';

/** One assignable staffer for the reassignment picker. */
interface AssignmentStaffOption {
  /** Normalized id (the wire sends a NUMBER — DNKADM-0064 `asId`). */
  id: string;
  /** Live display name; degrades to `''` (the row still renders). */
  name: string;
  /**
   * Free over this reservation's own effective interval. `false` = occupied →
   * render "(conflict)"; the write still ACCEPTS the pick (admin double-book,
   * DNKAPI-0357) — this is a warning, never a gate.
   */
  available: boolean;
  /** This reservation's OWN current staffer/Bay (self-excluded ⇒ always available). */
  currentlyAssigned: boolean;
}

/** One assignable resource ("bay"), with its remaining concurrency. */
interface AssignmentResourceOption extends AssignmentStaffOption {
  /**
   * Remaining concurrency over the interval IGNORING this booking (floored at 0),
   * or null when the read omits it — a capacity>1 Bay can show remaining slots.
   */
  capacityRemaining: number | null;
}

/**
 * The reassignment picker's single source of truth
 * (`GET …/{uuid}/assignment-options`, DNKAPI-0357). No need to combine
 * `staff-assignments`/`availability`.
 */
export interface AssignmentOptions {
  /** The service's staff mode, or null when absent/unknown. */
  staffMode: AssignmentMode | null;
  /** The service's resource mode, or null when absent/unknown. */
  resourceMode: AssignmentMode | null;
  /** The service's assignable staff at this location (empty when mode is `none`). */
  staff: AssignmentStaffOption[];
  /** The service's assignable resources at this location (empty when mode is `none`). */
  resources: AssignmentResourceOption[];
}

const ASSIGNMENT_MODES = new Set<AssignmentMode>(['member_selects', 'round_robin', 'none']);

/** Parse a raw assignment mode into a known value, or null (unknown/absent degrades). */
function asAssignmentMode(value: unknown): AssignmentMode | null {
  const s = asText(value)?.toLowerCase();
  return s !== undefined && ASSIGNMENT_MODES.has(s as AssignmentMode)
    ? (s as AssignmentMode)
    : null;
}

/**
 * Parse one picker option, or null without a usable id (mirrors `parseResource` —
 * an id-less entry can't be selected, so it is dropped rather than rendered). A
 * missing `available` degrades to `true`: the server is the authority on freeness
 * and the write never blocks, so a malformed read must not paint every option
 * "(conflict)".
 */
function parseAssignmentOption(raw: unknown): AssignmentStaffOption | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;
  return {
    id,
    name: asText(raw['name']) ?? '',
    available: asBoolean(raw['available'], true),
    currentlyAssigned: asBoolean(raw['currentlyAssigned'], false),
  };
}

/** Parse the resource twin — the staff shape plus a tolerated `capacityRemaining`. */
function parseAssignmentResourceOption(raw: unknown): AssignmentResourceOption | null {
  const base = parseAssignmentOption(raw);
  if (base === null) return null;
  const o = isObject(raw) ? raw : {};
  return { ...base, capacityRemaining: asNumber(o['capacityRemaining']) };
}

/** Drop the un-parseable entries of a raw option list; a non-array yields `[]`. */
function parseOptionList<T>(raw: unknown, parse: (entry: unknown) => T | null): T[] {
  if (!Array.isArray(raw)) return [];
  const out: T[] = [];
  for (const entry of raw) {
    const parsed = parse(entry);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseAssignmentOptions(raw: unknown): AssignmentOptions {
  const o = isObject(raw) ? raw : {};
  return {
    staffMode: asAssignmentMode(o['staffMode']),
    resourceMode: asAssignmentMode(o['resourceMode']),
    staff: parseOptionList(o['staff'], parseAssignmentOption),
    resources: parseOptionList(o['resources'], parseAssignmentResourceOption),
  };
}

/**
 * `GET /v2/admin/reservations/{uuid}/assignment-options` → the reassignment
 * picker's staff/resource pools with conflict annotation, parsed DEFENSIVELY
 * (tolerates empty lists, a missing `capacityRemaining`, unknown modes, and
 * id-less entries). Throws a typed `ApiError` (`403` foreign-or-unknown, `404`
 * terminal, `409 SERVICE_INACTIVE`).
 */
export async function getAssignmentOptions(uuid: string): Promise<AssignmentOptions> {
  const body = await request<unknown>('GET', `${reservationPath(uuid)}/assignment-options`);
  return parseAssignmentOptions(body);
}

/**
 * Reassign body (DNKAPI-0356/0357). A **partial** edit: an omitted dimension is
 * left unchanged, so the caller sends ONLY the dimension(s) it is changing. At
 * least one of `staffId`/`resourceId` is required. `reason` is audit-only
 * (optional — unlike the reschedule's REQUIRED reason).
 */
export interface ReassignReservationInput {
  /** The new staffer — omitted leaves the staff assignment unchanged. */
  staffId?: string;
  /** The new resource ("bay") — omitted leaves the resource assignment unchanged. */
  resourceId?: string;
  /** Optional audit reason (no reschedule is consumed either way). */
  reason?: string;
  /**
   * DNKAPI-0542: proceed despite a coach double-book. Sent ONLY when `true`; omitted
   * means the server's default — refuse with `409 STAFF_CONFLICT` (see
   * {@link parseStaffConflict}). Per request, never remembered.
   */
  acknowledgeConflict?: boolean;
  /**
   * DNKAPI-0549: proceed although the bay has no capacity left over this interval. Sent
   * ONLY when `true`; omitted means the server's default — refuse with
   * `409 RESOURCE_CONFLICT` (see {@link parseOverrideRefusal}). Per request, never
   * remembered, and INDEPENDENT of `acknowledgeConflict` above.
   */
  acknowledgeResourceConflict?: boolean;
  /**
   * DNKAPI-0549: proceed although the coach is outside the hours they work and/or past an
   * appointment cap. **One flag, BOTH reasons** (`STAFF_OUTSIDE_HOURS` and
   * `STAFF_OVER_CAP`) — there is no separate cap flag. Sent ONLY when `true`; omitted means
   * the server's default — refuse. Per request, never remembered.
   */
  acknowledgeStaffSchedule?: boolean;
}

/**
 * `PUT /v2/admin/reservations/{uuid}/assignment` → reassign the Staff and/or
 * Resource of a **confirmed** booking IN PLACE (DNKADM-0211). Distinct from
 * {@link updateReservation} (reschedule — time + required reason, consumes a
 * reschedule) and {@link updateReservationDetails} (unconfirmed only): same
 * time/service/variation/location/uuid, no reschedule-count, no refund-lock,
 * **money/credit-neutral**.
 *
 * **Every freeness gate is now refuse-unless-acknowledged.** DNKAPI-0357 dropped the gate
 * on both dimensions; DNKAPI-0542 restored the coach-occupancy half, and DNKAPI-0549
 * restored the other three. On this endpoint:
 *  - a `staffId` that CHANGES the coach onto an overlapping booking → `409 STAFF_CONFLICT`
 *    (the blocking booking(s) in `details.conflicts`) unless `acknowledgeConflict: true`;
 *  - a `resourceId` for a bay with **no capacity left** over this interval →
 *    `409 RESOURCE_CONFLICT` (the blocking booking(s) in `details.resourceConflicts`, a
 *    key of its own — never `conflicts`) unless `acknowledgeResourceConflict: true`.
 *    **This reverses DNKAPI-0357's "a full bay still saves with a warning".**
 *  - a `staffId` for a coach outside the hours they work and/or past an appointment cap →
 *    `409 STAFF_OUTSIDE_HOURS` / `STAFF_OVER_CAP` unless `acknowledgeStaffSchedule: true`
 *    — one flag, both codes.
 *
 * Several of these can apply to ONE request, and every applicable reason comes back in one
 * body under `overrideBlocks` — see {@link parseOverrideRefusal}, the single seam this
 * endpoint's hosts branch on. The other rejection is a pick outside the service's
 * assignable pool.
 *
 * The picker's `available:false` "(conflict)" annotation (from `assignment-options`) is the
 * **forecast**, not the verdict: it still warns rather than blocking, and the refusal above
 * is what actually decides the write.
 *
 * Sends only the present dimensions (+ `reason`) and **guards client-side**: with
 * neither id it throws the server's own `400 ASSIGNMENT_REQUIRED` shape WITHOUT
 * issuing a request, so callers branch status-first on one uniform error. Returns
 * the re-parsed reservation ({@link parseReservation} — the 200 is the same
 * enriched object as the list read). Throws a typed `ApiError`; every 4xx is
 * atomic (the reservation is unchanged).
 */
export async function reassignReservation(
  uuid: string,
  input: ReassignReservationInput,
): Promise<Reservation> {
  if (input.staffId === undefined && input.resourceId === undefined) {
    throw new ApiError('VALIDATION', 400, {
      details: {
        code: REASSIGN_ERROR_CODES.assignmentRequired,
        message: 'Choose a staff member or a bay to reassign.',
      },
    });
  }
  const json: Record<string, unknown> = {};
  if (input.staffId !== undefined) json['staffId'] = input.staffId;
  if (input.resourceId !== undefined) json['resourceId'] = input.resourceId;
  if (input.reason !== undefined) json['reason'] = input.reason;
  if (input.acknowledgeConflict === true) json['acknowledgeConflict'] = true;
  if (input.acknowledgeResourceConflict === true) json['acknowledgeResourceConflict'] = true;
  if (input.acknowledgeStaffSchedule === true) json['acknowledgeStaffSchedule'] = true;
  const body = await request<unknown>('PUT', `${reservationPath(uuid)}/assignment`, { json });
  return parseReservation(body);
}
